import {
  Camera,
  CameraHelper,
  DirectionalLight,
  DirectionalLightHelper,
  Light,
} from 'three';
import { gsap } from 'gsap';

import { Beaune } from './ranges/beaune/Beaune.js';
import { Bussy } from './ranges/bussy/Bussy.js';
import { Chagny } from './ranges/chagny/Chagny.js';
import { Chagny1400Left } from './ranges/chagny1400left/Chagny1400Left.js';
import { Chagny1400Right } from './ranges/chagny1400right/Chagny1400Right.js';
import { Chagny1800 } from './ranges/chagny1800/Chagny1800.js';
import { Chambertin } from './ranges/chambertin/Chambertin.js';
import { Chassagne } from './ranges/chassagne/Chassagne.js';
import { Citeaux } from './ranges/citeaux/Citeaux.js';
import { Cluny } from './ranges/cluny/Cluny.js';
import { Cluny1400Left } from './ranges/cluny1400left/Cluny1400Left.js';
import { Cluny1400Right } from './ranges/cluny1400right/Cluny1400Right.js';
import { Cluny1800 } from './ranges/cluny1800/Cluny1800.js';
import { Cormatin } from './ranges/cormatin/Cormatin.js';
import { Fontenay } from './ranges/fontenay/Fontenay.js';
import { Rully } from './ranges/rully/Rully.js';
import { Saulieu } from './ranges/saulieu/Saulieu.js';
import { Savigny } from './ranges/savigny/Savigny.js';
import { Sully } from './ranges/sully/Sully.js';
import { Sully2200 } from './ranges/sully2200/Sully2200.js';
import { Sully1800Left } from './ranges/sully1800left/Sully1800Left.js';
import { Sully1800Right } from './ranges/sully1800right/Sully1800Right.js';
import { Volnay } from './ranges/volnay/Volnay.js';
import { Vougeot } from './ranges/vougeot/Vougeot.js';
import { BaseRange } from './ranges/shared/BaseRange.js';

import { BaseHood } from './hoods/BaseHood.js';
import { Hood900 } from './hoods/Hood900.js';
import { Hood1100 } from './hoods/Hood1100.js';
import { Hood1200 } from './hoods/Hood1200.js';
import { Hood1300 } from './hoods/Hood1300.js';
import { Hood1600 } from './hoods/Hood1600.js';
import { Hood1700 } from './hoods/Hood1700.js';
import { Hood2000 } from './hoods/Hood2000.js';
import { Hood2400 } from './hoods/Hood2400.js';

import { BaseBacksplash } from './backsplashes/BaseBacksplash.js';
import { Backsplash700 } from './backsplashes/Backsplash700.js';
import { Backsplash900 } from './backsplashes/Backsplash900.js';
import { Backsplash1000 } from './backsplashes/Backsplash1000.js';
import { Backsplash1100 } from './backsplashes/Backsplash1100.js';
import { Backsplash1400 } from './backsplashes/Backsplash1400.js';
import { Backsplash1500 } from './backsplashes/Backsplash1500.js';
import { Backsplash1800 } from './backsplashes/Backsplash1800.js';
import { Backsplash2200 } from './backsplashes/Backsplash2200.js';

import { RangeParts } from './ranges/parts/RangeParts.js';
import { KnobModels } from './ranges/shared/KnobModels.js';
import { RangeTopOptions } from './ranges/shared/RangeTopOptions.js';
import { Rules } from './ranges/shared/Rules.js';
import { AssetLoader } from './shared/AssetLoader.js';
import { BacksplashState } from './shared/BacksplashState.js';
import { BacksplashSummary } from './shared/BacksplashSummary.js';
import {
  Burner,
  Color,
  Cupboard,
  Gas,
  RangeHeight,
  Line,
  Message,
  RangeModel,
  Oven,
  RangeTop,
  Spacer,
  Trim,
  Tab,
  HoodModelNumber,
  BacksplashModelNumber,
  HoodInsertChoice,
  HoodDuctCoverFinish,
  HoodDuctCoverHeight,
} from './shared/Enums.js';
import { HoodState } from './shared/HoodState.js';
import { HoodSummary } from './shared/HoodSummary.js';
import { loadEnvMap } from './shared/loadEnvMap.js';
import { Materials } from './shared/Materials.js';
import { LacancheLogo } from './shared/SharedParts.js';
import { RangeState } from './shared/RangeState.js';
import { RangeSummary } from './shared/RangeSummary.js';
import { Summary } from './shared/Summary.js';
import { Url } from './shared/Url.js';

import { createCamera } from './components/camera.js';
import { createLights } from './components/lights.js';
import { createScene } from './components/scene.js';

import { createControls } from './systems/controls.js';
import { createRenderer } from './systems/renderer.js';
import { HelperGUI } from './systems/HelperGUI.js';
import { Loop } from './systems/Loop.js';
import { Resizer } from './systems/Resizer.js';

export class World {
  /** @type {AssetLoader} */
  #assets;

  /** @type {BaseBacksplash} */
  #backsplash;

  /** @type {BacksplashState} */
  #backsplashState;

  /** @type {BacksplashSummary} */
  #backsplashSummary;

  /** @type {BaseHood} */
  #hood;

  /** @type {HoodState} */
  #hoodState;

  /** @type {HoodSummary} */
  #hoodSummary;

  /** @type {KnobModels} */
  #knobModels;

  /** @type {Materials} */
  #materials;

  /** @type {BaseRange} */
  #range;

  /** @type {RangeParts} */
  #rangeParts;

  /** @type {RangeState} */
  #rangeState;

  /** @type {RangeSummary} */
  #rangeSummary;

  /** @type {RangeTopOptions} */
  #rangeTopOptions;

  /** @type {Rules} */
  #rules;

  /** @type {LacancheLogo} */
  #sharedParts;

  /** @type {Summary} */
  #summary;

  /** @type {Url} */
  #url;

  #camera;
  #controls;
  #defaultZoom = 1.4;
  #loop;
  #renderer;
  #scene;
  #resizer;

  #hoodCreator;
  #backsplashCreator;

  // @ts-ignore
  #gui;

  constructor(container) {
    this.#url = new Url();
    this.#rangeState = new RangeState(this.#url);
    this.#hoodState = new HoodState();
    this.#backsplashState = new BacksplashState();
    this.#rules = new Rules(this.#rangeState);
    this.#assets = new AssetLoader();
    this.#camera = createCamera(this.#url);
    this.#renderer = createRenderer();
    this.#scene = createScene(this.#url);
    this.#scene.position.y = -0.5;
    this.#loop = new Loop(this.#camera, this.#scene, this.#renderer);
    this.#summary = new Summary(
      this.#rangeState,
      this.#hoodState,
      this.#backsplashState
    );
    // this.#gui = new HelperGUI();
    // this.#gui.gui.hide();

    container.appendChild(this.#renderer.domElement);
    this.#controls = createControls(this.#camera, this.#renderer.domElement);

    const { frontLeftLight, frontRightLight, hemisphereLight } = createLights();

    if (this.#url.isSummary()) this.#setupSummaryPage();

    if (this.#url.isRangeSummary()) this.#rangeState.showLabels = true;

    if (this.#url.isScreenshot()) {
      this.#rangeState.showLabels = false;
    } else {
      this.#loop.updatables.push(this.#controls);
    }

    this.#scene.add(frontLeftLight, frontRightLight, hemisphereLight);

    // @ts-ignore
    this.#resizer = new Resizer(container, this.#camera, this.#renderer);

    // this.#gui.takeScreenshot();
    // this.#gui.addCamera(this.#camera);
    // this.#gui.addHemiLight(hemisphereLight);
    // this.#gui.addLeftLight(frontLeftLight);
    // this.#gui.addRightLight(frontRightLight);
    // this.#gui.exportGLB(this.#scene);

    // this.#addCameraHelper(this.#camera);
    // this.#addDirectionalLightHelper(leftLight);
    // this.#addShadowHelper(rightLight);
  }

  async init() {
    let line, rangeModel, requestedRange, hoodModel, backsplashModel;
    const configuredRange = this.#url.configuredRange();
    const configuredHood = this.#url.configuredHood();
    const configuredBacksplash = this.#url.configuredBacksplash();

    await this.#assets.loadMinimum();

    this.#materials = new Materials(this.#assets);
    this.#rangeTopOptions = new RangeTopOptions(this.#assets, this.#materials);
    this.#knobModels = new KnobModels(this.#assets, this.#materials);
    this.#rangeParts = new RangeParts(this.#assets, this.#materials);
    this.#sharedParts = new LacancheLogo(this.#assets, this.#materials);

    if ((requestedRange = this.#url.requestedRange())) {
      rangeModel = requestedRange;
    } else if (configuredRange) {
      rangeModel = configuredRange.model;
      this.#rangeState.setState(configuredRange);
    } else if (!this.#url.isSummary()) {
      rangeModel = RangeModel.sully;
    } else {
      this.#rangeState.isSelected = false;
    }

    if ((line = this.#url.requestedLine())) {
      this.#rangeState.line = line;
      this.#rangeState.trim = line === Line.moderne ? Trim.chrome : Trim.brass;
    }
    this.#updateTrimOptions(this.#rangeState.line);

    if (rangeModel) {
      [this.#range, this.#hoodCreator, this.#backsplashCreator] =
        await this.#switchRange(rangeModel);
    }

    if (configuredHood) {
      hoodModel = configuredHood.modelNumber;
      this.#hoodState.setState(configuredHood);
      this.#hood = await this.#createHood(hoodModel);
      this.#updateHoodCheckbox();
      this.#updateHoodInsert();
      this.#updateHoodDuctCoverFinish();
      this.#updateHoodDuctCoverHeight();
    }

    if (configuredBacksplash) {
      backsplashModel = configuredBacksplash.modelNumber;
      this.#backsplashState.setState(configuredBacksplash);
      this.#backsplash = await this.#createBacksplash(backsplashModel);
    }

    if (this.#url.isSummary()) {
      this.#showOrHideSummaryTabs();
      this.#adjust3DModelViewForSummary();
    }

    // TODO: Clean this up
    this.#rangeState.camera = this.#camera;

    // this.#gui.addMaterial(this.#range);
    // this.#gui.addLogo(this.#range);

    if (!this.#url.isScreenshot()) {
      this.initializeEventListeners();
    }

    if (this.#url.isRangeSummary() && !this.#url.isScreenshot()) {
      this.#range.displayLabels(true);
      this.#updateShowLabelsCheckbox(true);
    }

    if (this.#url.wantsFrontView()) {
      this.#camera.position.set(0, 0.135, 4.5);
      this.#controls.update();

      if (this.#url.wantsLabels()) {
        this.#range?.displayFrontLabels();
      }
    }

    if (this.#url.wantsTopView()) {
      this.#camera.position.set(0, 4.61, 0.2);
      this.#controls.update();

      if (this.#url.wantsLabels()) {
        this.#range?.displayTopLabels();
      }
    }

    // Display top and front labels for the "tilted" range view
    if (
      !this.#url.wantsTopView() &&
      !this.#url.wantsFrontView() &&
      this.#url.wantsLabels()
    ) {
      this.#range?.displayLabels(true);
    }

    // Scroll the 3D model into view on mobile
    if (window.innerWidth <= 640) {
      document.getElementById('top-half').scrollIntoView();
      this.#controls.enableZoom = true;
    }

    loadEnvMap(this.#scene, () => {
      //   this.#gui.addEnvironmentMap(this.#scene);
    });

    const delay = this.#url.isLocal() ? 20 : 5_000;

    // Use the following DIV to notify Browsershot that the range is loaded.
    if (this.#url.isScreenshot()) {
      setTimeout(() => {
        this.#renderer.render(this.#scene, this.#camera);
        if (this.#url.isLocal()) this.#loop.start();
        const rangeLoadedDiv = document.createElement('div');
        rangeLoadedDiv.id = 'range-loaded';
        document.body.appendChild(rangeLoadedDiv);
      }, delay);
    }
  }

  initializeEventListeners() {
    document.getElementById('model').addEventListener('change', async (e) => {
      // @ts-ignore
      const model = e.target.value;

      this.#rangeState.isConfigured = false;

      // Remove the old range and its labels from the scene
      this.#scene.remove(
        this.#range.range,
        this.#range.labels.front,
        this.#range.labels.top
      );

      if (this.#hood) {
        this.#hoodState.isSelected = false;
        this.#removeHood();
        this.#showOrHideHoodOptions();
      }

      if (this.#backsplash) {
        this.#backsplashState.isSelected = false;
        this.#removeBacksplash();
      }

      [this.#range, this.#hoodCreator, this.#backsplashCreator] =
        await this.#switchRange(model);

      this.#setCameraPositionAndZoom(model);

      // Reset the URL
      history.replaceState(null, '', '/');
    });

    // Change the line
    document.getElementById('line').addEventListener('change', (e) => {
      // @ts-ignore
      const line = e.target.value;

      this.#rangeState.line = line;
      this.#rangeState.isConfigured = false;
      this.#range.changeLine(line);

      if (
        line === Line.moderne &&
        ![Trim.brushedSS, Trim.chrome].includes(this.#rangeState.trim)
      ) {
        this.#range.changeTrim(Trim.chrome);
        this.#rangeState.trim = Trim.chrome;
      }

      this.#updateTrimOptions(line);
      this.#updateRangeSummary();
    });

    // Change the base range top
    document.getElementById('top').addEventListener('change', (e) => {
      // @ts-ignore
      const top = e.target.value;
      const leftOptions = document.getElementById('left-option');
      // @ts-ignore
      const selectedOption = leftOptions.value;
      // @ts-ignore
      const options = leftOptions.options;

      if (
        this.#rules.hasTraditionalPlateToRightOfSully4Feux({
          top: top,
        })
      ) {
        this.#showDialog(Message.traditionalPlateNextTo4Feux);
        this.#updateRightOptionSelection();
        return;
      }

      if (
        this.#rules.hasInductionRingsInProhibitedPositions({
          top: top,
        })
      ) {
        this.#showDialog(Message.inductionRingsInProhibitedPosition);
        this.#updateFarLeftOptionSelection();
        return;
      }

      if (this.#topHasIncompatibleBurnerToLeft(top)) {
        return;
      }

      this.#rangeState.top = top;
      this.#rangeState.isConfigured = false;
      this.#range.changeRangeTop(top);

      if (top === RangeTop.fourFeux) {
        // Enable the 18k burner, plancha and induction rings options
        options[3].disabled = false;
        options[4].disabled = false;
        options[5].disabled = false;
      } else {
        // Disable the 18k burner, plancha and induction rings options
        options[3].disabled = true;
        options[4].disabled = true;
        options[5].disabled = true;

        if (
          [
            Burner.one18kBurner,
            Burner.electricPlancha,
            Burner.twoInductionRings,
          ].includes(selectedOption)
        ) {
          // Select stainless steel workstation
          options[6].selected = true;
          this.#range.changeOption2(Burner.stainlessSteelWorkstation);
        }
      }

      this.#updateRangeSummary();
    });

    // Change the trim
    document.getElementById('trim').addEventListener('change', (e) => {
      // @ts-ignore
      const trim = e.target.value;

      this.#rangeState.trim = trim;
      this.#rangeState.isConfigured = false;
      this.#hoodState.trim = trim;

      this.#range.changeTrim(trim);
      if (this.#hood) {
        this.#hood.changeTrim(trim);
      }

      this.#updateRangeSummary();
    });

    // Change the color
    document.getElementById('color').addEventListener('change', (e) => {
      // @ts-ignore
      const color = e.target.value;

      this.#rangeState.color = color;
      this.#rangeState.isConfigured = false;
      this.#hoodState.color = color;

      this.#range.changeColor(color);
      if (this.#hood) {
        this.#hood.changeColor(color);
      }

      this.#updateRangeSummary();
      this.#updateHoodSummary();

      // this.#gui.changeMaterial(this.#range);
    });

    // Change the left oven
    document.getElementById('left-oven').addEventListener('change', (e) => {
      // @ts-ignore
      const ovenType = e.target.value;

      if (this.#rules.hasLeftElectricRightGasOvens({ leftOven: ovenType })) {
        this.#showDialog(Message.leftElectricRightGasOvens);
        this.#updateLeftOvenSelection();
        return;
      }

      if (this.#rules.isOverMaxAmps({ leftOven: ovenType })) {
        this.#showDialog(Message.exceedsMaxRangeAmps);
        this.#updateLeftOvenSelection();
        return;
      }

      if (this.#rules.hasPlanchaOverGasOven({ leftOven: ovenType })) {
        this.#showDialog(Message.planchaOverGasOven);
        this.#updateLeftOvenSelection();
        return;
      }

      if (this.#rules.hasInductionRingsOverGasOven({ leftOven: ovenType })) {
        this.#showDialog(Message.inductionRingsOverGasOven);
        this.#updateLeftOvenSelection();
        return;
      }

      this.#rangeState.leftOven = ovenType;
      this.#rangeState.isConfigured = false;
      this.#range.changeLeftOven(ovenType);

      this.#updateRangeSummary();
    });

    // Change the right oven
    document.getElementById('right-oven').addEventListener('change', (e) => {
      // @ts-ignore
      const ovenType = e.target.value;

      if (this.#rules.hasLeftElectricRightGasOvens({ rightOven: ovenType })) {
        this.#showDialog(Message.leftElectricRightGasOvens);
        this.#updateRightOvenSelection();
        return;
      }

      if (this.#rules.isOverMaxAmps({ rightOven: ovenType })) {
        this.#showDialog(Message.exceedsMaxRangeAmps);
        this.#updateRightOvenSelection();
        return;
      }

      if (this.#rules.hasPlanchaOverGasOven({ rightOven: ovenType })) {
        this.#showDialog(Message.planchaOverGasOven);
        this.#updateRightOvenSelection();
        return;
      }

      if (this.#rules.hasInductionRingsOverGasOven({ rightOven: ovenType })) {
        this.#showDialog(Message.inductionRingsOverGasOven);
        this.#updateRightOvenSelection();
        return;
      }

      this.#rangeState.rightOven = ovenType;
      this.#rangeState.isConfigured = false;
      this.#range.changeRightOven(ovenType);

      this.#updateRangeSummary();
    });

    // Change the back spacer
    document.getElementById('back-spacer').addEventListener('change', (e) => {
      // @ts-ignore
      const spacerElevation = e.target.value;

      this.#rangeState.isConfigured = false;

      this.#range.changeSpacer(spacerElevation);
      this.#rangeState.spacer = spacerElevation;
    });

    // Change the far left range top option. This will always be over the left cupboard.
    document
      .getElementById('far-left-option')
      .addEventListener('change', (e) => {
        // @ts-ignore
        const farLeftOption = e.target.value;

        if (this.#rules.isOverMaxAmps({ option1: farLeftOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateFarLeftOptionSelection();
          return;
        }

        if (
          this.#rules.hasMultiCookerOverWarmingCupboard({
            option1: farLeftOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverWarmingCupboard);
          this.#updateFarLeftOptionSelection();
          return;
        }

        if (
          this.#rules.hasMultiCookerOverVCT({
            option1: farLeftOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverVCT);
          this.#updateFarLeftOptionSelection();
          return;
        }

        if (
          this.#rules.hasFlameGrillOverVCT({
            option1: farLeftOption,
          })
        ) {
          this.#showDialog(Message.flameGrillOverVCT);
          this.#updateFarLeftOptionSelection();
          return;
        }

        if (
          this.#rules.hasInductionRingsInProhibitedPositions({
            option1: farLeftOption,
          })
        ) {
          this.#showDialog(Message.inductionRingsInProhibitedPosition);
          this.#updateFarLeftOptionSelection();
          return;
        }

        this.#rangeState.option1 = farLeftOption;
        this.#rangeState.isConfigured = false;
        this.#range.changeOption1(farLeftOption);

        this.#updateRangeSummary();
      });

    // Change the left range top option
    document.getElementById('left-option').addEventListener('change', (e) => {
      // @ts-ignore
      const leftOption = e.target.value;
      // @ts-ignore
      const model = document.getElementById('model').value;

      if (this.#leftOptionExceedsMaxAmps(leftOption, model)) {
        return;
      }

      if (this.#leftOptionIsPlanchaOverGasOven(leftOption, model)) {
        return;
      }

      if (this.#leftOptionIsInductionRingsOverGasOven(leftOption, model)) {
        return;
      }

      switch (model) {
        case RangeModel.cluny1800:
        case RangeModel.sully1800left:
        case RangeModel.sully2200:
          this.#rangeState.option2 = leftOption;
          this.#range.changeOption2(leftOption);
          break;

        case RangeModel.sully:
        case RangeModel.sully1800right:
          this.#rangeState.option1 = leftOption;
          this.#range.changeOption1(leftOption);
          break;
      }

      this.#rangeState.isConfigured = false;

      this.#updateRangeSummary();
    });

    // Change the right range top option
    document.getElementById('right-option').addEventListener('change', (e) => {
      // @ts-ignore
      const rightOption = e.target.value;
      // @ts-ignore
      const model = document.getElementById('model').value;

      if (this.#rightOptionExceedsMaxAmps(rightOption, model)) {
        return;
      }

      if (this.#rightOptionIsPlanchaOverGasOven(rightOption, model)) {
        return;
      }

      if (this.#rightOptionIsInductionRingsOverGasOven(rightOption, model)) {
        return;
      }

      if (this.#rightOptionIsTradPlateNextTo4Feux(rightOption, model)) {
        return;
      }

      switch (model) {
        case RangeModel.sully1800left:
        case RangeModel.sully2200:
          this.#rangeState.option3 = rightOption;
          this.#range.changeOption3(rightOption);
          break;

        case RangeModel.sully:
        case RangeModel.sully1800right:
          this.#rangeState.option2 = rightOption;
          this.#range.changeOption2(rightOption);
          break;
      }

      this.#rangeState.isConfigured = false;

      this.#updateRangeSummary();
    });

    // Change the far right range top option
    document
      .getElementById('far-right-option')
      .addEventListener('change', (e) => {
        // @ts-ignore
        const farRightOption = e.target.value;
        const rightCupboard = document.getElementById('right-cupboard');
        // @ts-ignore
        const model = document.getElementById('model').value;

        if (this.#farRightOptionExceedsMaxAmps(farRightOption, model)) {
          return;
        }

        if (
          this.#farRightMultiCookerOverWarmingCupboard(farRightOption, model)
        ) {
          return;
        }

        if (this.#farRightMultiCookerOverVCT(farRightOption, model)) {
          return;
        }

        if (this.#farRightFlameGrillOverVCT(farRightOption, model)) {
          return;
        }

        if (
          this.#farRightInductionRingsInProhibitedPositions(
            farRightOption,
            model
          )
        ) {
          return;
        }

        switch (model) {
          case RangeModel.chagny1400right:
          case RangeModel.chambertin:
          case RangeModel.cluny1400right:
          case RangeModel.savigny:
            this.#rangeState.option1 = farRightOption;
            this.#range.changeOption1(farRightOption);
            break;
          case RangeModel.chagny1800:
          case RangeModel.citeaux:
          case RangeModel.cluny1800:
          case RangeModel.fontenay:
            this.#rangeState.option2 = farRightOption;
            this.#range.changeOption2(farRightOption);
            break;
          case RangeModel.sully1800right:
            this.#rangeState.option3 = farRightOption;
            this.#range.changeOption3(farRightOption);
            break;
          case RangeModel.sully2200:
            this.#rangeState.option4 = farRightOption;
            this.#range.changeOption4(farRightOption);
            break;
        }

        this.#rangeState.isConfigured = false;

        this.#updateRangeSummary();
      });

    // Change the left cupboard
    document.getElementById('left-cupboard').addEventListener('change', (e) => {
      // @ts-ignore
      const leftCupboard = e.target.value;

      if (this.#rules.isOverMaxAmps({ leftCupboard: leftCupboard })) {
        this.#showDialog(Message.exceedsMaxRangeAmps);
        this.#updateLeftCupboardSelection();
        return;
      }

      if (
        this.#rules.hasMultiCookerOverWarmingCupboard({
          leftCupboard: leftCupboard,
        })
      ) {
        this.#showDialog(Message.multiCookerOverWarmingCupboard);
        this.#updateLeftCupboardSelection();
        return;
      }

      this.#rangeState.leftCupboard = leftCupboard;
      this.#rangeState.isConfigured = false;
      this.#range.changeLeftCupboard(leftCupboard);

      this.#updateRangeSummary();
    });

    // Change the right cupboard
    document
      .getElementById('right-cupboard')
      .addEventListener('change', (e) => {
        // @ts-ignore
        const rightCupboard = e.target.value;

        if (this.#rules.isOverMaxAmps({ rightCupboard: rightCupboard })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateRightCupboardSelection();
          return;
        }

        if (
          this.#rules.hasMultiCookerOverWarmingCupboard({
            rightCupboard: rightCupboard,
          })
        ) {
          this.#showDialog(Message.multiCookerOverWarmingCupboard);
          this.#updateRightCupboardSelection();
          return;
        }

        this.#rangeState.rightCupboard = rightCupboard;
        this.#rangeState.isConfigured = false;
        this.#range.changeRightCupboard(rightCupboard);

        this.#updateRangeSummary();
      });

    // Change the gas type
    document.getElementById('gas-type').addEventListener('change', (e) => {
      // @ts-ignore
      const gasType = e.target.value;

      this.#rangeState.gas = gasType;
      this.#rangeState.isConfigured = false;

      this.#updateRangeSummary();
    });

    // Change the range height
    document.getElementById('height').addEventListener('change', (e) => {
      // @ts-ignore
      const rangeHeight = e.target.value;

      this.#rangeState.height = rangeHeight;
      this.#rangeState.isConfigured = false;

      this.#updateRangeSummary();
    });

    // Rotate the range to the side view
    document.getElementById('side-view-btn').addEventListener('click', (e) => {
      const frontViewBtn = document.getElementById('front-view-btn');
      const topViewBtn = document.getElementById('top-view-btn');

      // @ts-ignore
      e.target.classList.add('border-2', 'border-[#203b44]');
      frontViewBtn.classList.remove('border-2', 'border-[#203b44]');
      topViewBtn.classList.remove('border-2', 'border-[#203b44]');

      gsap.to(this.#camera.position, {
        duration: 0.5,
        x: -2.7,
        y: 1.5,
        z: 3.6,
      });

      gsap.to(this.#camera, {
        duration: 0.5,
        zoom: this.#defaultZoom,
        onUpdate: () => {
          this.#camera.updateProjectionMatrix();
        },
      });
    });

    // Rotate the range to the front view
    document.getElementById('front-view-btn').addEventListener('click', (e) => {
      const sideViewBtn = document.getElementById('side-view-btn');
      const topViewBtn = document.getElementById('top-view-btn');

      // @ts-ignore
      e.target.classList.add('border-2', 'border-[#203b44]');
      sideViewBtn.classList.remove('border-2', 'border-[#203b44]');
      topViewBtn.classList.remove('border-2', 'border-[#203b44]');

      gsap.to(this.#camera.position, {
        duration: 0.5,
        x: 0,
        y: 0.4,
        z: 4.7,
      });

      gsap.to(this.#camera, {
        duration: 0.5,
        zoom: this.#defaultZoom,
        onUpdate: () => {
          this.#camera.updateProjectionMatrix();
        },
      });
    });

    // Rotate the range to the top view
    document.getElementById('top-view-btn').addEventListener('click', (e) => {
      const sideViewBtn = document.getElementById('side-view-btn');
      const frontViewBtn = document.getElementById('front-view-btn');

      // @ts-ignore
      e.target.classList.add('border-2', 'border-[#203b44]');
      sideViewBtn.classList.remove('border-2', 'border-[#203b44]');
      frontViewBtn.classList.remove('border-2', 'border-[#203b44]');

      gsap.to(this.#camera.position, {
        duration: 0.75,
        x: 0,
        y: 5,
        z: 0.2,
      });

      gsap.to(this.#camera, {
        duration: 0.75,
        zoom: this.#defaultZoom,
        onUpdate: () => {
          this.#camera.updateProjectionMatrix();
        },
      });
    });

    // Zoom in to the range
    document.getElementById('zoom-in-btn').addEventListener('click', () => {
      const newZoom = Math.min(this.#camera.zoom * 1.5, 5);

      gsap.to(this.#camera, {
        duration: 0.5,
        zoom: newZoom,
        onUpdate: () => {
          this.#camera.updateProjectionMatrix();
        },
      });
    });

    // Zoom out from the range
    document.getElementById('zoom-out-btn').addEventListener('click', () => {
      const newZoom = Math.max(this.#camera.zoom / 1.5, 0.5);

      gsap.to(this.#camera, {
        duration: 0.5,
        zoom: newZoom,
        onUpdate: () => {
          this.#camera.updateProjectionMatrix();
        },
      });
    });

    // Hide/Show the range labels based on the checkbox
    document.getElementById('show-labels').addEventListener('change', (e) => {
      // @ts-ignore
      if (e.target.checked) {
        this.#rangeState.showLabels = true;
        this.#range.displayLabels(true);
      } else {
        this.#rangeState.showLabels = false;
        this.#range.displayLabels(false);
      }
    });

    // Hide/Show the range labels based on the summary page button
    document.getElementById('toggle-labels').addEventListener('click', (e) => {
      const toggleLabelsBtn = document.getElementById('toggle-labels');
      this.#rangeState.showLabels = !this.#rangeState.showLabels;
      this.#range.displayLabels(this.#rangeState.showLabels);
      this.#updateShowLabelsCheckbox(this.#rangeState.showLabels);
      this.#update2DRangeImages();

      if (this.#rangeState.showLabels) {
        toggleLabelsBtn.innerText = 'Hide Labels';
      } else {
        toggleLabelsBtn.innerText = 'Show Labels';
      }
    });

    // On the summary page, allow the range to be modified
    document.getElementById('modify-range').addEventListener('click', (e) => {
      this.#undoSummaryPage();
      document.querySelector('.right-column').scrollIntoView();
    });

    // On the summary page, go to the form for a price quote
    document
      .getElementById('go-to-quote-form-btn')
      .addEventListener('click', (e) => {
        this.#undoSummaryPage();
        document.getElementById('req-form-intro').scrollIntoView();
      });
  }

  async init2() {
    if (this.#url.isScreenshot()) {
      this.#loop.stop();
      return;
    }

    // Show/Hide the hood
    document.getElementById('hood').addEventListener('change', async (e) => {
      /** @type HTMLInputElement */
      // @ts-ignore
      const hoodCheckbox = e.target;

      this.#hoodState.isSelected = hoodCheckbox.checked;

      if (this.#hoodState.isSelected) {
        // Create and add the hood
        this.#hood = this.#hoodCreator();
        await this.#hood.init();
        this.#scene.add(this.#hood.hood);
      } else {
        this.#removeHood();
      }

      this.#setCameraPositionAndZoom();
      this.#showOrHideHoodOptions();

      // If the hood is selected, ensure that the summary tabs are visible
      this.#showOrHideSummaryTabs();

      this.#updateSummary();
    });

    // Change the hood insert
    document.getElementById('hood-insert').addEventListener('change', (e) => {
      // @ts-ignore
      const hoodInsert = e.target.value;

      this.#hoodState.insertChoice = hoodInsert;
      this.#hood.changeInsert(hoodInsert);

      this.#updateHoodSummary();
    });

    // Change the hood duct cover finish
    document
      .getElementById('duct-cover-finish')
      .addEventListener('change', (e) => {
        // @ts-ignore
        const ductCoverFinish = e.target.value;

        this.#hoodState.ductCoverFinish = ductCoverFinish;
        this.#hood.changeDuctCoverFinish(ductCoverFinish);

        this.#updateHoodSummary();
      });

    // Change the hood duct cover height
    document
      .getElementById('duct-cover-height')
      .addEventListener('change', (e) => {
        // @ts-ignore
        const ductCoverHeight = e.target.value;

        this.#hoodState.ductCoverHeight = ductCoverHeight;
        this.#hood.changeDuctCoverHeight(ductCoverHeight);

        this.#updateHoodSummary();
      });

    // Show/Hide the backsplash
    document
      .getElementById('backsplash')
      .addEventListener('change', async (e) => {
        /** @type HTMLInputElement */
        // @ts-ignore
        const backsplashCheckbox = e.target;

        this.#backsplashState.isSelected = backsplashCheckbox.checked;

        if (this.#backsplashState.isSelected) {
          // Create and add the backsplash
          this.#backsplash = this.#backsplashCreator();
          await this.#backsplash.init();
          this.#scene.add(this.#backsplash.backsplash);
        } else {
          this.#removeBacksplash();
        }

        this.#setCameraPositionAndZoom();

        // If the backsplash is selected, ensure that the summary tabs are visible
        this.#showOrHideSummaryTabs();

        this.#updateSummary();
      });

    // Show the Range summary
    document
      .getElementById('range-summary-tab')
      .addEventListener('click', (e) => {
        e.preventDefault();
        this.#makeTabActive(Tab.range);
      });

    // Show the Hood summary
    document
      .getElementById('hood-summary-tab')
      .addEventListener('click', (e) => {
        e.preventDefault();
        this.#makeTabActive(Tab.hood);
      });

    // Show the Backsplash summary
    document
      .getElementById('backsplash-summary-tab')
      .addEventListener('click', (e) => {
        e.preventDefault();
        this.#makeTabActive(Tab.backsplash);
      });

    // Show the chosen summary for mobile devices
    document
      .getElementById('summary-select-mobile')
      .addEventListener('change', (e) => {
        // @ts-ignore
        const selectedTab = e.target.value;
        this.#makeTabActive(selectedTab);
      });

    // Submit the price request form
    const priceRequestForm = document.getElementById('price-quote-form');
    priceRequestForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const errorMessage = document.getElementById('price-quote-error');
      errorMessage.style.display = 'none';

      // @ts-ignore
      const priceRequestData = new FormData(e.target);
      priceRequestData.set('model', this.#rangeState.model);
      priceRequestData.set('line', this.#rangeState.line);
      priceRequestData.set('top', this.#rangeState.top);
      priceRequestData.set('trim', this.#rangeState.trim);
      priceRequestData.set('color', this.#rangeState.color);
      priceRequestData.set('leftOven', this.#rangeState.leftOven);
      if (this.#rangeState.rightOven !== null) {
        priceRequestData.set('rightOven', this.#rangeState.rightOven);
      }
      if (this.#rangeState.petiteOven !== null) {
        priceRequestData.set('petiteOven', this.#rangeState.petiteOven);
      }
      if (this.#rangeState.leftCupboard !== null) {
        priceRequestData.set('leftCupboard', this.#rangeState.leftCupboard);
      }
      if (this.#rangeState.rightCupboard !== null) {
        priceRequestData.set('rightCupboard', this.#rangeState.rightCupboard);
      }
      if (this.#rangeState.option1 !== null) {
        priceRequestData.set('option1', this.#rangeState.option1);
      }
      if (this.#rangeState.option2 !== null) {
        priceRequestData.set('option2', this.#rangeState.option2);
      }
      if (this.#rangeState.option3 !== null) {
        priceRequestData.set('option3', this.#rangeState.option3);
      }
      if (this.#rangeState.option4 !== null) {
        priceRequestData.set('option4', this.#rangeState.option4);
      }
      priceRequestData.set('gas', this.#rangeState.gas);
      priceRequestData.set('height', this.#rangeState.height);

      fetch('/api/price-quote', {
        method: 'POST',
        body: priceRequestData,
      })
        .then((response) => response.json())
        .then((json) => {
          if (json.success) {
            const successMessage = document.getElementById(
              'price-quote-success'
            );
            successMessage.style.display = 'block';
            successMessage.innerText = json.success;

            // @ts-ignore
            priceRequestForm.reset();

            setTimeout(() => {
              successMessage.style.display = 'none';
            }, 30_000);

            // Notify Google AdWords that a range quote was requested
            // @ts-ignore
            gtag('event', 'conversion', {
              send_to: 'AW-1069754766/QhiKCL2jzLUYEI7TjP4D',
            });
          } else if (json.error) {
            errorMessage.style.display = 'block';
            errorMessage.innerText = json.error;
          }
        })
        .catch((error) => console.error(error));
    });

    // "Go to" the summary page
    document.getElementById('summary-link').addEventListener('click', (e) => {
      e.preventDefault();
      history.pushState(null, '', this.#summary.summaryLink());
      location.reload();
    });

    document
      .getElementById('copy-range-link')
      .addEventListener('click', (e) => {
        e.preventDefault();
        // @ts-ignore
        const rangeLink = document.getElementById('summary-link').href;
        navigator.clipboard.writeText(rangeLink);
      });
  }

  render() {
    this.#renderer.render(this.#scene, this.#camera);
  }

  start() {
    this.#loop.start();
  }

  stop() {
    this.#loop.stop();
  }

  /**
   * For a given range model, return the range, hood creator and backsplash
   * creator. Update the UI for the new range model.
   * @param {?string} rangeModel
   * @returns {Promise<[BaseRange, Function, Function]>}
   */
  async #switchRange(rangeModel) {
    this.#loop.purgeOldLabels();

    const [range, hoodCreator, backsplashCreator] =
      this.#get3DModelCreatorsForRangeModel(rangeModel);

    await range.init();

    range.changeLine(this.#rangeState.line);
    range.changeRangeTop(this.#rangeState.top);
    range.changeTrim(this.#rangeState.trim);
    range.changeColor(this.#rangeState.color);
    range.changeSpacer(this.#rangeState.spacer);
    range.displayLabels(this.#rangeState.showLabels);

    this.#scene.add(range.range, range.labels.front, range.labels.top);

    this.#loop.updatables.push(range.labels);

    this.#resetBurnerOptionSelections();
    this.#showRangeOptions(range);
    this.#assignBurnerOptions();
    this.#updateModelSelection();
    this.#updateLineSelection();
    this.#updateRangeTopSelection();
    this.#updateTrimSelection();
    this.#updateColorSelection();
    this.#updateFarLeftOptionSelection();
    this.#updateLeftOptionSelection();
    this.#updateRightOptionSelection();
    this.#updateFarRightOptionSelection();
    this.#updateLeftOvenSelection();
    this.#updateRightOvenSelection();
    this.#updateLeftCupboardSelection();
    this.#updateRightCupboardSelection();
    this.#updateHeightSelection();
    this.#updateGasTypeSelection();
    this.#updateBackSpacerSelection();

    if (!this.#url.isScreenshot()) {
      this.#rangeSummary = new RangeSummary(this.#rangeState);
      this.#hoodSummary = new HoodSummary(this.#hoodState);
      this.#backsplashSummary = new BacksplashSummary(this.#backsplashState);

      this.#updateRangeSummary();
      this.#updateHoodSummary();
      this.#updateBacksplashSummary();
      this.#updateSummary();
    }

    return [range, hoodCreator, backsplashCreator];
  }

  /**
   * For a given hood model, create the hood, add it to the scene, and return it.
   * @param {string} hoodModel
   * @returns {Promise<BaseHood>}
   */
  async #createHood(hoodModel) {
    let hood;

    switch (hoodModel) {
      case HoodModelNumber.LH900:
        hood = this.#createHood900();
        break;

      case HoodModelNumber.LH1100:
        hood = this.#createHood1100();
        break;

      case HoodModelNumber.LH1200:
        hood = this.#createHood1200();
        break;

      case HoodModelNumber.LH1300:
        hood = this.#createHood1300();
        break;

      case HoodModelNumber.LH1600:
        hood = this.#createHood1600();
        break;

      case HoodModelNumber.LH1700:
        hood = this.#createHood1700();
        break;

      case HoodModelNumber.LH2000:
        hood = this.#createHood2000();
        break;

      case HoodModelNumber.LH2400:
        hood = this.#createHood2400();
        break;
    }

    await hood.init();
    this.#scene.add(hood.hood);

    return hood;
  }

  /**
   * For a given backsplash model, create the backsplash, add it to the scene,
   * and return it.
   * @param {string} backsplashModel
   * @returns {Promise<BaseBacksplash>}
   */
  async #createBacksplash(backsplashModel) {
    let backsplash;

    switch (backsplashModel) {
      case BacksplashModelNumber.LCHD700:
        backsplash = this.#createBacksplash700();
        break;

      case BacksplashModelNumber.LCHD900:
        backsplash = this.#createBacksplash900();
        break;

      case BacksplashModelNumber.LCHD1000:
        backsplash = this.#createBacksplash1000();
        break;

      case BacksplashModelNumber.LCHD1100:
        backsplash = this.#createBacksplash1100();
        break;

      case BacksplashModelNumber.LCHD1400:
        backsplash = this.#createBacksplash1400();
        break;

      case BacksplashModelNumber.LCHD1500:
        backsplash = this.#createBacksplash1500();
        break;

      case BacksplashModelNumber.LCHD1800:
        backsplash = this.#createBacksplash1800();
        break;

      case BacksplashModelNumber.LCHD2200:
        backsplash = this.#createBacksplash2200();
        break;
    }

    await backsplash.init();
    this.#scene.add(backsplash.backsplash);

    return backsplash;
  }

  /**
   * For a given range model, return the range, hood creator and backsplash creator
   * @param {?string} model
   * @returns {[BaseRange, Function, Function]}
   */
  #get3DModelCreatorsForRangeModel(model) {
    let range, hoodCreator, backsplashCreator;

    switch (model) {
      case RangeModel.beaune:
        range = this.#createBeaune();
        hoodCreator = this.#createHood1100;
        backsplashCreator = this.#createBacksplash900;
        this.#rangeState.model = RangeModel.beaune;
        this.#hoodState.modelNumber = HoodModelNumber.LH1100;
        this.#hoodState.insertChoice = HoodInsertChoice.modernAire;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD900;
        break;

      case RangeModel.bussy:
        range = this.#createBussy();
        hoodCreator = this.#createHood1100;
        backsplashCreator = this.#createBacksplash900;
        this.#rangeState.model = RangeModel.bussy;
        this.#hoodState.modelNumber = HoodModelNumber.LH1100;
        this.#hoodState.insertChoice = HoodInsertChoice.modernAire;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD900;
        break;

      case RangeModel.chagny:
        range = this.#createChagny();
        hoodCreator = this.#createHood1200;
        backsplashCreator = this.#createBacksplash1000;
        this.#rangeState.model = RangeModel.chagny;
        this.#hoodState.modelNumber = HoodModelNumber.LH1200;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1000;
        break;

      case RangeModel.chagny1400left:
        range = this.#createChagny1400Left();
        hoodCreator = this.#createHood1600;
        backsplashCreator = this.#createBacksplash1400;
        this.#rangeState.model = RangeModel.chagny1400left;
        this.#hoodState.modelNumber = HoodModelNumber.LH1600;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1400;
        break;

      case RangeModel.chagny1400right:
        range = this.#createChagny1400Right();
        hoodCreator = this.#createHood1600;
        backsplashCreator = this.#createBacksplash1400;
        this.#rangeState.model = RangeModel.chagny1400right;
        this.#hoodState.modelNumber = HoodModelNumber.LH1600;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1400;
        break;

      case RangeModel.chagny1800:
        range = this.#createChagny1800();
        hoodCreator = this.#createHood2000;
        backsplashCreator = this.#createBacksplash1800;
        this.#rangeState.model = RangeModel.chagny1800;
        this.#hoodState.modelNumber = HoodModelNumber.LH2000;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1800;
        break;

      case RangeModel.chambertin:
        range = this.#createChambertin();
        hoodCreator = this.#createHood1300;
        backsplashCreator = this.#createBacksplash1100;
        this.#rangeState.model = RangeModel.chambertin;
        this.#hoodState.modelNumber = HoodModelNumber.LH1300;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1100;
        break;

      case RangeModel.chassagne:
        range = this.#createChassagne();
        hoodCreator = this.#createHood1300;
        backsplashCreator = this.#createBacksplash1100;
        this.#rangeState.model = RangeModel.chassagne;
        this.#hoodState.modelNumber = HoodModelNumber.LH1300;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1100;
        break;

      case RangeModel.citeaux:
        range = this.#createCiteaux();
        hoodCreator = this.#createHood1700;
        backsplashCreator = this.#createBacksplash1500;
        this.#rangeState.model = RangeModel.citeaux;
        this.#hoodState.modelNumber = HoodModelNumber.LH1700;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1500;
        break;

      case RangeModel.cluny:
        range = this.#createCluny();
        hoodCreator = this.#createHood1200;
        backsplashCreator = this.#createBacksplash1000;
        this.#rangeState.model = RangeModel.cluny;
        this.#hoodState.modelNumber = HoodModelNumber.LH1200;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1000;
        break;

      case RangeModel.cluny1400left:
        range = this.#createCluny1400Left();
        hoodCreator = this.#createHood1600;
        backsplashCreator = this.#createBacksplash1400;
        this.#rangeState.model = RangeModel.cluny1400left;
        this.#hoodState.modelNumber = HoodModelNumber.LH1600;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1400;
        break;

      case RangeModel.cluny1400right:
        range = this.#createCluny1400Right();
        hoodCreator = this.#createHood1600;
        backsplashCreator = this.#createBacksplash1400;
        this.#rangeState.model = RangeModel.cluny1400right;
        this.#hoodState.modelNumber = HoodModelNumber.LH1600;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1400;
        break;

      case RangeModel.cluny1800:
        range = this.#createCluny1800();
        hoodCreator = this.#createHood2000;
        backsplashCreator = this.#createBacksplash1800;
        this.#rangeState.model = RangeModel.cluny1800;
        this.#hoodState.modelNumber = HoodModelNumber.LH2000;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1800;
        break;

      case RangeModel.cormatin:
        range = this.#createCormatin();
        hoodCreator = this.#createHood900;
        backsplashCreator = this.#createBacksplash700;
        this.#rangeState.model = RangeModel.cormatin;
        this.#hoodState.modelNumber = HoodModelNumber.LH900;
        this.#hoodState.insertChoice = HoodInsertChoice.modernAire;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD700;
        break;

      case RangeModel.fontenay:
        range = this.#createFontenay();
        hoodCreator = this.#createHood1700;
        backsplashCreator = this.#createBacksplash1500;
        this.#rangeState.model = RangeModel.fontenay;
        this.#hoodState.modelNumber = HoodModelNumber.LH1700;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1500;
        break;

      case RangeModel.rully:
        range = this.#createRully();
        hoodCreator = this.#createHood900;
        backsplashCreator = this.#createBacksplash700;
        this.#rangeState.model = RangeModel.rully;
        this.#hoodState.modelNumber = HoodModelNumber.LH900;
        this.#hoodState.insertChoice = HoodInsertChoice.modernAire;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD700;
        break;

      case RangeModel.saulieu:
        range = this.#createSaulieu();
        hoodCreator = this.#createHood1300;
        backsplashCreator = this.#createBacksplash1100;
        this.#rangeState.model = RangeModel.saulieu;
        this.#hoodState.modelNumber = HoodModelNumber.LH1300;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1100;
        break;

      case RangeModel.savigny:
        range = this.#createSavigny();
        hoodCreator = this.#createHood1300;
        backsplashCreator = this.#createBacksplash1100;
        this.#rangeState.model = RangeModel.savigny;
        this.#hoodState.modelNumber = HoodModelNumber.LH1300;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1100;
        break;

      case RangeModel.sully:
        range = this.#createSully();
        hoodCreator = this.#createHood1600;
        backsplashCreator = this.#createBacksplash1400;
        this.#rangeState.model = RangeModel.sully;
        this.#hoodState.modelNumber = HoodModelNumber.LH1600;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1400;
        break;

      case RangeModel.sully1800left:
        range = this.#createSully1800Left();
        hoodCreator = this.#createHood2000;
        backsplashCreator = this.#createBacksplash1800;
        this.#rangeState.model = RangeModel.sully1800left;
        this.#hoodState.modelNumber = HoodModelNumber.LH2000;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1800;
        break;

      case RangeModel.sully1800right:
        range = this.#createSully1800Right();
        hoodCreator = this.#createHood2000;
        backsplashCreator = this.#createBacksplash1800;
        this.#rangeState.model = RangeModel.sully1800right;
        this.#hoodState.modelNumber = HoodModelNumber.LH2000;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1800;
        break;

      case RangeModel.sully2200:
        range = this.#createSully2200();
        hoodCreator = this.#createHood2400;
        backsplashCreator = this.#createBacksplash2200;
        this.#rangeState.model = RangeModel.sully2200;
        this.#hoodState.modelNumber = HoodModelNumber.LH2400;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD2200;
        break;

      case RangeModel.volnay:
        range = this.#createVolnay();
        hoodCreator = this.#createHood1200;
        backsplashCreator = this.#createBacksplash1000;
        this.#rangeState.model = RangeModel.volnay;
        this.#hoodState.modelNumber = HoodModelNumber.LH1200;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1000;
        break;

      case RangeModel.vougeot:
        range = this.#createVougeot();
        hoodCreator = this.#createHood1200;
        backsplashCreator = this.#createBacksplash1000;
        this.#rangeState.model = RangeModel.vougeot;
        this.#hoodState.modelNumber = HoodModelNumber.LH1200;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1000;
        break;

      default:
        range = this.#createSully();
        hoodCreator = this.#createHood1600;
        backsplashCreator = this.#createBacksplash1400;
        this.#rangeState.model = RangeModel.sully;
        this.#hoodState.modelNumber = HoodModelNumber.LH1600;
        this.#hoodState.insertChoice = HoodInsertChoice.ventAHoodMagicLung;
        this.#backsplashState.modelNumber = BacksplashModelNumber.LCHD1400;
        break;
    }

    return [range, hoodCreator, backsplashCreator];
  }

  /***************        Create Ranges        ***************/

  /**
   * Create a Beaune range
   * @returns {Beaune}
   */
  #createBeaune() {
    return new Beaune(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Bussy range
   * @returns {Bussy}
   */
  #createBussy() {
    return new Bussy(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Chagny range
   * @returns {Chagny}
   */
  #createChagny() {
    return new Chagny(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Chagny 1400 (left) range
   * @returns {Chagny1400Left}
   */
  #createChagny1400Left() {
    return new Chagny1400Left(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Chagny 1400 (right) range
   * @returns {Chagny1400Right}
   */
  #createChagny1400Right() {
    return new Chagny1400Right(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Chagny range
   * @returns {Chagny1800}
   */
  #createChagny1800() {
    return new Chagny1800(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Chambertin range
   * @returns {Chambertin}
   */
  #createChambertin() {
    return new Chambertin(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Chassagne range
   * @returns {Chassagne}
   */
  #createChassagne() {
    return new Chassagne(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Citeaux range
   * @returns {Citeaux}
   */
  #createCiteaux() {
    return new Citeaux(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Cluny range
   * @returns {Cluny}
   */
  #createCluny() {
    return new Cluny(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Cluny 1400 (left) range
   * @returns {Cluny1400Left}
   */
  #createCluny1400Left() {
    return new Cluny1400Left(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Cluny 1400 (right) range
   * @returns {Cluny1400Right}
   */
  #createCluny1400Right() {
    return new Cluny1400Right(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Cluny 1800 range
   * @returns {Cluny1800}
   */
  #createCluny1800() {
    return new Cluny1800(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Cormatin range
   * @returns {Cormatin}
   */
  #createCormatin() {
    return new Cormatin(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Fontenay range
   * @returns {Fontenay}
   */
  #createFontenay() {
    return new Fontenay(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Rully range
   * @returns {Rully}
   */
  #createRully() {
    return new Rully(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Saulieu range
   * @returns {Saulieu}
   */
  #createSaulieu() {
    return new Saulieu(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Savigny range
   * @returns {Savigny}
   */
  #createSavigny() {
    return new Savigny(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Sully range
   * @returns {Sully}
   */
  #createSully() {
    return new Sully(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Sully 1800 (left) range
   * @returns {Sully1800Left}
   */
  #createSully1800Left() {
    return new Sully1800Left(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Sully 1800 (right) range
   * @returns {Sully1800Right}
   */
  #createSully1800Right() {
    return new Sully1800Right(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Sully 2200 range
   * @returns {Sully2200}
   */
  #createSully2200() {
    return new Sully2200(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Volnay range
   * @returns {Volnay}
   */
  #createVolnay() {
    return new Volnay(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /**
   * Create a Vougeot range
   * @returns {Vougeot}
   */
  #createVougeot() {
    return new Vougeot(
      this.#assets,
      this.#materials,
      this.#rangeParts,
      this.#sharedParts,
      this.#rangeTopOptions,
      this.#knobModels,
      this.#rangeState
    );
  }

  /***************           Create Hoods            ***************/

  #createHood900() {
    return new Hood900(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood1100() {
    return new Hood1100(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood1200() {
    return new Hood1200(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood1300() {
    return new Hood1300(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood1600() {
    return new Hood1600(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood1700() {
    return new Hood1700(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood2000() {
    return new Hood2000(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  #createHood2400() {
    return new Hood2400(
      this.#assets,
      this.#materials,
      this.#sharedParts,
      this.#hoodState
    );
  }

  /***************        Create Backsplashes        ***************/

  #createBacksplash700() {
    return new Backsplash700(this.#assets, this.#materials);
  }

  #createBacksplash900() {
    return new Backsplash900(this.#assets, this.#materials);
  }

  #createBacksplash1000() {
    return new Backsplash1000(this.#assets, this.#materials);
  }

  #createBacksplash1100() {
    return new Backsplash1100(this.#assets, this.#materials);
  }

  #createBacksplash1400() {
    return new Backsplash1400(this.#assets, this.#materials);
  }

  #createBacksplash1500() {
    return new Backsplash1500(this.#assets, this.#materials);
  }

  #createBacksplash1800() {
    return new Backsplash1800(this.#assets, this.#materials);
  }

  #createBacksplash2200() {
    return new Backsplash2200(this.#assets, this.#materials);
  }

  /**
   * Disable the options of brass and nickel for moderne ranges
   * @param {string} line - classique or moderne
   */
  #updateTrimOptions(line) {
    const trimSelect = document.getElementById('trim');
    // @ts-ignore
    const trim = trimSelect.value;
    // @ts-ignore
    const options = trimSelect.options;

    if (line === Line.moderne) {
      // Disable the brass and nickel options
      options[0].disabled = true;
      options[3].disabled = true;

      if (![Trim.brushedSS, Trim.chrome].includes(trim)) {
        // Select chrome
        options[2].selected = true;
      }
    } else if (line === Line.classique) {
      // Enable the brass and nickel options
      options[0].disabled = false;
      options[3].disabled = false;
    }
  }

  /**
   * Hide or show the optional burner choices and cupboard choices depending on
   * what a range has. Disable the 4 feux when not supported
   * @param {BaseRange} range
   */
  #showRangeOptions(range) {
    // @ts-ignore
    const rangeTopOptions = document.getElementById('top').options;
    const farLeftOptionBlock = document.getElementById('far-left-option-block');
    const leftOptionBlock = document.getElementById('left-option-block');
    const rightOptionBlock = document.getElementById('right-option-block');
    const farRightOptionBlock = document.getElementById(
      'far-right-option-block'
    );
    const leftOvenLabel = document.getElementById('left-oven-label');
    const leftOvenOptions = document.getElementById('left-oven');
    const rightOvenBlock = document.getElementById('right-oven-block');
    const rightOvenOptions = document.getElementById('right-oven');
    const leftCupboardBlock = document.getElementById('left-cupboard-block');
    const rightCupboardBlock = document.getElementById('right-cupboard-block');

    farLeftOptionBlock.style.display = range.features.farLeftOption
      ? 'block'
      : 'none';
    leftOptionBlock.style.display = range.features.leftOption
      ? 'block'
      : 'none';
    rightOptionBlock.style.display = range.features.rightOption
      ? 'block'
      : 'none';
    farRightOptionBlock.style.display = range.features.farRightOption
      ? 'block'
      : 'none';
    leftOvenLabel.innerText = range.features.rightOven ? 'Left Oven' : 'Oven';
    rightOvenBlock.style.display = range.features.rightOven ? 'block' : 'none';
    leftCupboardBlock.style.display = range.features.leftCupboard
      ? 'block'
      : 'none';
    rightCupboardBlock.style.display = range.features.rightCupboard
      ? 'block'
      : 'none';

    // Traditional range top option
    rangeTopOptions[1].style.display = range.features.traditional
      ? 'block'
      : 'none';
    rangeTopOptions[1].disabled = !range.features.traditional;

    // 4 Feux range top option
    rangeTopOptions[2].style.display = range.features.fourFeux
      ? 'block'
      : 'none';
    rangeTopOptions[2].disabled = !range.features.fourFeux;

    // Classique Reverse range top option
    rangeTopOptions[3].style.display = range.features.classiqueRev
      ? 'block'
      : 'none';
    rangeTopOptions[3].disabled = !range.features.classiqueRev;

    // Left Gas Oven option
    leftOvenOptions[0].style.display = range.features.noGasOven
      ? 'none'
      : 'block';
    leftOvenOptions[0].disabled = range.features.noGasOven;

    // Left Electric Convection Oven option
    leftOvenOptions[1].style.display = range.features.convection
      ? 'block'
      : 'none';
    leftOvenOptions[1].disabled = !range.features.convection;

    // Left Electric Oven option
    leftOvenOptions[2].style.display = range.features.convection
      ? 'none'
      : 'block';
    leftOvenOptions[2].disabled = range.features.convection;

    // Right Electric Convection Oven option
    rightOvenOptions[1].style.display = range.features.convection
      ? 'block'
      : 'none';
    rightOvenOptions[1].disabled = !range.features.convection;

    // Right Electric Oven option
    rightOvenOptions[2].style.display = range.features.convection
      ? 'none'
      : 'block';
    rightOvenOptions[2].disabled = range.features.convection;

    // For one oven ranges, make electric (or convection) the default
    leftOvenOptions[1].selected =
      !range.features.rightOven && range.features.convection;
    leftOvenOptions[2].selected =
      !range.features.rightOven && !range.features.convection;

    // For two oven ranges, select right electric if not convection
    rightOvenOptions[2].selected =
      range.features.rightOven && !range.features.convection;

    // For "no gas" left ovens, select electric if not convection
    if (range.features.noGasOven && !range.features.convection) {
      leftOvenOptions[2].selected = true;
      rightOvenOptions[0].selected = true; // Select gas for right oven
    }
  }

  /**
   * Select the proper burner option for "assigned" optional burners
   */
  #assignBurnerOptions() {
    const farLeftOption = document.getElementById('far-left-option');
    const leftOption = document.getElementById('left-option');

    switch (this.#rangeState.model) {
      case RangeModel.sully:
      case RangeModel.sully1800left:
      case RangeModel.sully1800right:
      case RangeModel.sully2200:
        leftOption[2].selected = true; // Select the 15k BTU burners
        break;
      case RangeModel.fontenay:
      case RangeModel.citeaux:
        farLeftOption[2].selected = true; // Select the 15k BTU burners
        break;
    }
  }

  /**
   * Hide or show the hood options depending on if a hood is selected. Hide the
   * Vent-A-Hood options if the hood doesn't support them.
   */
  #showOrHideHoodOptions() {
    const hoodInsertDiv = document.getElementById('hood-insert-div');
    const ductCoverFinishDiv = document.getElementById('duct-cover-finish-div');
    const ductCoverHeightDiv = document.getElementById('duct-cover-height-div');
    // @ts-ignore
    const hoodInsertOptions = document.getElementById('hood-insert').options;

    hoodInsertDiv.style.display = this.#hoodState.isSelected ? 'block' : 'none';
    ductCoverFinishDiv.style.display = this.#hoodState.isSelected
      ? 'block'
      : 'none';
    ductCoverHeightDiv.style.display = this.#hoodState.isSelected
      ? 'block'
      : 'none';

    if (this.#hood) {
      // Vent-A-Hood Magic Lung option
      hoodInsertOptions[0].style.display = this.#hood.features.ventAHood
        ? 'block'
        : 'none';
      hoodInsertOptions[0].disabled = !this.#hood.features.ventAHood;

      // Vent-A-Hood single motor option
      hoodInsertOptions[1].style.display = this.#hood.features.ventAHood
        ? 'block'
        : 'none';
      hoodInsertOptions[1].disabled = !this.#hood.features.ventAHood;

      // Vent-A-Hood dual motor option
      hoodInsertOptions[2].style.display = this.#hood.features.ventAHood
        ? 'block'
        : 'none';
      hoodInsertOptions[2].disabled = !this.#hood.features.ventAHood;

      // ModernAire option
      hoodInsertOptions[3].selected = !this.#hood.features.ventAHood;
    }
  }

  #removeHood() {
    // @ts-ignore
    document.getElementById('hood').checked = false;
    this.#scene.remove(this.#scene.getObjectByName(this.#hood.hood.name));
    this.#hood = null;
  }

  #removeBacksplash() {
    // @ts-ignore
    document.getElementById('backsplash').checked = false;
    this.#scene.remove(
      this.#scene.getObjectByName(this.#backsplash.backsplash.name)
    );
    this.#backsplash = null;
  }

  #updateModelSelection() {
    const model = document.getElementById('model');
    // @ts-ignore
    const modelOptions = model.options;
    // @ts-ignore
    const modelValue = model.value;

    if (modelValue === this.#rangeState.model) return;

    switch (this.#rangeState.model) {
      case RangeModel.cormatin:
        modelOptions[0].selected = true;
        break;
      case RangeModel.rully:
        modelOptions[1].selected = true;
        break;
      case RangeModel.beaune:
        modelOptions[2].selected = true;
        break;
      case RangeModel.bussy:
        modelOptions[3].selected = true;
        break;
      case RangeModel.cluny:
        modelOptions[4].selected = true;
        break;
      case RangeModel.chagny:
        modelOptions[5].selected = true;
        break;
      case RangeModel.volnay:
        modelOptions[6].selected = true;
        break;
      case RangeModel.vougeot:
        modelOptions[7].selected = true;
        break;
      case RangeModel.chambertin:
        modelOptions[8].selected = true;
        break;
      case RangeModel.chassagne:
        modelOptions[9].selected = true;
        break;
      case RangeModel.savigny:
        modelOptions[10].selected = true;
        break;
      case RangeModel.saulieu:
        modelOptions[11].selected = true;
        break;
      case RangeModel.sully:
        modelOptions[12].selected = true;
        break;
      case RangeModel.cluny1400left:
        modelOptions[13].selected = true;
        break;
      case RangeModel.cluny1400right:
        modelOptions[14].selected = true;
        break;
      case RangeModel.chagny1400left:
        modelOptions[15].selected = true;
        break;
      case RangeModel.chagny1400right:
        modelOptions[16].selected = true;
        break;
      case RangeModel.fontenay:
        modelOptions[17].selected = true;
        break;
      case RangeModel.citeaux:
        modelOptions[18].selected = true;
        break;
      case RangeModel.cluny1800:
        modelOptions[19].selected = true;
        break;
      case RangeModel.chagny1800:
        modelOptions[20].selected = true;
        break;
      case RangeModel.sully1800left:
        modelOptions[21].selected = true;
        break;
      case RangeModel.sully1800right:
        modelOptions[22].selected = true;
        break;
      case RangeModel.sully2200:
        modelOptions[23].selected = true;
        break;
    }
  }

  #updateLineSelection() {
    // @ts-ignore
    const line = document.getElementById('line').options;

    switch (this.#rangeState.line) {
      case Line.classique:
        line[0].selected = true;
        break;
      case Line.moderne:
        line[1].selected = true;
        break;
    }
  }

  #updateRangeTopSelection() {
    // @ts-ignore
    const topOptions = document.getElementById('top').options;

    switch (this.#rangeState.top) {
      case RangeTop.classique:
        topOptions[0].selected = true;
        break;
      case RangeTop.traditional:
        topOptions[1].selected = true;
        break;
      case RangeTop.fourFeux:
        topOptions[2].selected = true;
        break;
      case RangeTop.classiqueRev:
        topOptions[3].selected = true;
        break;
    }
  }

  #updateTrimSelection() {
    // @ts-ignore
    const trim = document.getElementById('trim').options;

    switch (this.#rangeState.trim) {
      case Trim.brass:
        trim[0].selected = true;
        break;
      case Trim.brushedSS:
        trim[1].selected = true;
        break;
      case Trim.chrome:
        trim[2].selected = true;
        break;
      case Trim.nickel:
        trim[3].selected = true;
        break;
    }
  }

  #updateColorSelection() {
    // @ts-ignore
    const color = document.getElementById('color').options;

    switch (this.#rangeState.color) {
      case Color.anthracite:
        color[0].selected = true;
        break;
      case Color.ardoise:
        color[1].selected = true;
        break;
      case Color.armor:
        color[2].selected = true;
        break;
      case Color.britishRacingGreen:
        color[3].selected = true;
        break;
      case Color.burgundyRed:
        color[4].selected = true;
        break;
      case Color.chantilly:
        color[5].selected = true;
        break;
      case Color.chocolat:
        color[6].selected = true;
        break;
      case Color.coralBlue:
        color[7].selected = true;
        break;
      case Color.delftBlue:
        color[8].selected = true;
        break;
      case Color.englishCreme:
        color[9].selected = true;
        break;
      case Color.faience:
        color[10].selected = true;
        break;
      case Color.frangipane:
        color[11].selected = true;
        break;
      case Color.frenchBlue:
        color[12].selected = true;
        break;
      case Color.griotte:
        color[13].selected = true;
        break;
      case Color.ivory:
        color[14].selected = true;
        break;
      case Color.mandarine:
        color[15].selected = true;
        break;
      case Color.marronGlace:
        color[16].selected = true;
        break;
      case Color.matteBlack:
        color[17].selected = true;
        break;
      case Color.olive:
        color[18].selected = true;
        break;
      case Color.plum:
        color[19].selected = true;
        break;
      case Color.portugueseBlue:
        color[20].selected = true;
        break;
      case Color.provenceYellow:
        color[21].selected = true;
        break;
      case Color.roseQuartz:
        color[22].selected = true;
        break;
      case Color.shinyBlack:
        color[23].selected = true;
        break;
      case Color.stainlessSteel:
        color[24].selected = true;
        break;
      case Color.terracotta:
        color[25].selected = true;
        break;
      case Color.tilleul:
        color[26].selected = true;
        break;
      case Color.vertSilice:
        color[27].selected = true;
        break;
      case Color.vertSologne:
        color[28].selected = true;
        break;
      case Color.white:
        color[29].selected = true;
        break;
    }
  }

  #updateFarLeftOptionSelection() {
    // @ts-ignore
    const farLeftOption = document.getElementById('far-left-option').options;

    if (
      this.#rangeState.model === RangeModel.chagny1400left ||
      this.#rangeState.model === RangeModel.chagny1800 ||
      this.#rangeState.model === RangeModel.chassagne ||
      this.#rangeState.model === RangeModel.citeaux ||
      this.#rangeState.model === RangeModel.cluny1400left ||
      this.#rangeState.model === RangeModel.cluny1800 ||
      this.#rangeState.model === RangeModel.fontenay ||
      this.#rangeState.model === RangeModel.saulieu ||
      this.#rangeState.model === RangeModel.sully1800left ||
      this.#rangeState.model === RangeModel.sully2200
    ) {
      switch (this.#rangeState.option1) {
        case Burner.stainlessSteelWorkstation:
          farLeftOption[0].selected = true;
          break;
        case Burner.two11kBurners:
          farLeftOption[1].selected = true;
          break;
        case Burner.two15kBurners:
          farLeftOption[2].selected = true;
          break;
        case Burner.one18kBurner:
          farLeftOption[3].selected = true;
          break;
        case Burner.flameGrill:
          farLeftOption[4].selected = true;
          break;
        case Burner.electricPlancha:
          farLeftOption[5].selected = true;
          break;
        case Burner.traditionalSimmerPlate:
          farLeftOption[6].selected = true;
          break;
        case Burner.multiCooker:
          farLeftOption[7].selected = true;
          break;
        case Burner.twoInductionRings:
          farLeftOption[8].selected = true;
          break;
      }
    }
  }

  #updateLeftOptionSelection() {
    // @ts-ignore
    const leftOption = document.getElementById('left-option').options;
    let targetOption = null;

    if (
      this.#rangeState.model === RangeModel.sully ||
      this.#rangeState.model === RangeModel.sully1800right
    ) {
      targetOption = this.#rangeState.option1;
    } else if (
      this.#rangeState.model === RangeModel.sully1800left ||
      this.#rangeState.model === RangeModel.sully2200
    ) {
      targetOption = this.#rangeState.option2;
    }

    if (targetOption === null) return;

    switch (targetOption) {
      case Burner.stainlessSteelWorkstation:
        leftOption[0].selected = true;
        break;
      case Burner.two11kBurners:
        leftOption[1].selected = true;
        break;
      case Burner.two15kBurners:
        leftOption[2].selected = true;
        break;
      case Burner.one18kBurner:
        leftOption[3].selected = true;
        break;
      case Burner.electricPlancha:
        leftOption[4].selected = true;
        break;
      case Burner.twoInductionRings:
        leftOption[5].selected = true;
        break;
    }
  }

  #updateRightOptionSelection() {
    // @ts-ignore
    const rightOption = document.getElementById('right-option').options;
    let targetOption = null;

    if (
      this.#rangeState.model === RangeModel.sully ||
      this.#rangeState.model === RangeModel.sully1800right
    ) {
      targetOption = this.#rangeState.option2;
    } else if (
      this.#rangeState.model === RangeModel.sully1800left ||
      this.#rangeState.model === RangeModel.sully2200
    ) {
      targetOption = this.#rangeState.option3;
    }

    if (targetOption === null) return;

    switch (targetOption) {
      case Burner.stainlessSteelWorkstation:
        rightOption[0].selected = true;
        break;
      case Burner.two11kBurners:
        rightOption[1].selected = true;
        break;
      case Burner.two15kBurners:
        rightOption[2].selected = true;
        break;
      case Burner.one18kBurner:
        rightOption[3].selected = true;
        break;
      case Burner.electricPlancha:
        rightOption[4].selected = true;
        break;
      case Burner.traditionalSimmerPlate:
        rightOption[5].selected = true;
        break;
      case Burner.twoInductionRings:
        rightOption[6].selected = true;
        break;
    }
  }

  #updateFarRightOptionSelection() {
    // @ts-ignore
    const farRightOption = document.getElementById('far-right-option').options;
    let targetOption = null;

    if (
      this.#rangeState.model === RangeModel.chagny1400right ||
      this.#rangeState.model === RangeModel.chambertin ||
      this.#rangeState.model === RangeModel.cluny1400right ||
      this.#rangeState.model === RangeModel.savigny
    ) {
      targetOption = this.#rangeState.option1;
    } else if (
      this.#rangeState.model === RangeModel.chagny1800 ||
      this.#rangeState.model === RangeModel.citeaux ||
      this.#rangeState.model === RangeModel.cluny1800 ||
      this.#rangeState.model === RangeModel.fontenay
    ) {
      targetOption = this.#rangeState.option2;
    } else if (this.#rangeState.model === RangeModel.sully1800right) {
      targetOption = this.#rangeState.option3;
    } else if (this.#rangeState.model === RangeModel.sully2200) {
      targetOption = this.#rangeState.option4;
    }

    if (targetOption === null) return;

    switch (targetOption) {
      case Burner.stainlessSteelWorkstation:
        farRightOption[0].selected = true;
        break;
      case Burner.two11kBurners:
        farRightOption[1].selected = true;
        break;
      case Burner.two15kBurners:
        farRightOption[2].selected = true;
        break;
      case Burner.one18kBurner:
        farRightOption[3].selected = true;
        break;
      case Burner.flameGrill:
        farRightOption[4].selected = true;
        break;
      case Burner.electricPlancha:
        farRightOption[5].selected = true;
        break;
      case Burner.traditionalSimmerPlate:
        farRightOption[6].selected = true;
        break;
      case Burner.multiCooker:
        farRightOption[7].selected = true;
        break;
      case Burner.twoInductionRings:
        farRightOption[8].selected = true;
        break;
    }
  }

  #updateLeftOvenSelection() {
    // @ts-ignore
    const leftOven = document.getElementById('left-oven').options;

    switch (this.#rangeState.leftOven) {
      case Oven.gas:
        leftOven[0].selected = true;
        break;
      case Oven.convection:
        leftOven[1].selected = true;
        break;
      case Oven.electric:
        leftOven[2].selected = true;
        break;
    }
  }

  #updateRightOvenSelection() {
    // @ts-ignore
    const rightOven = document.getElementById('right-oven').options;

    switch (this.#rangeState.rightOven) {
      case Oven.gas:
        rightOven[0].selected = true;
        break;
      case Oven.convection:
        rightOven[1].selected = true;
        break;
      case Oven.electric:
        rightOven[2].selected = true;
        break;
    }
  }

  #updateLeftCupboardSelection() {
    // @ts-ignore
    const rightOven = document.getElementById('left-cupboard').options;

    switch (this.#rangeState.leftCupboard) {
      case Cupboard.warming:
        rightOven[0].selected = true;
        break;
      case Cupboard.storage:
        rightOven[1].selected = true;
        break;
    }
  }

  #updateRightCupboardSelection() {
    // @ts-ignore
    const rightOven = document.getElementById('right-cupboard').options;

    switch (this.#rangeState.rightCupboard) {
      case Cupboard.warming:
        rightOven[0].selected = true;
        break;
      case Cupboard.storage:
        rightOven[1].selected = true;
        break;
    }
  }

  #updateHeightSelection() {
    // @ts-ignore
    const height = document.getElementById('height').options;

    switch (this.#rangeState.height) {
      case RangeHeight[36]:
        height[0].selected = true;
        break;
      case RangeHeight['36_58']:
        height[1].selected = true;
        break;
      case RangeHeight.notSure:
        height[2].selected = true;
        break;
      case RangeHeight['35_38']:
      case RangeHeight['37_316']:
      case RangeHeight['37_1316']:
      case RangeHeight['38_38']:
      case RangeHeight[39]:
      case RangeHeight['39_916']:
      case RangeHeight['40_316']:
      case RangeHeight.other:
        height[3].selected = true;
        break;
    }
  }

  #updateGasTypeSelection() {
    // @ts-ignore
    const gasType = document.getElementById('gas-type').options;

    switch (this.#rangeState.gas) {
      case Gas.naturalGas:
        gasType[0].selected = true;
        break;
      case Gas.propane:
        gasType[1].selected = true;
        break;
      case Gas.notSure:
        gasType[2].selected = true;
        break;
    }
  }

  #updateBackSpacerSelection() {
    // @ts-ignore
    const backSpacer = document.getElementById('back-spacer').options;

    switch (this.#rangeState.spacer) {
      case Spacer.raised:
        backSpacer[0].selected = true;
        break;
      case Spacer.flush:
        backSpacer[1].selected = true;
        break;
    }
  }

  #updateHoodCheckbox() {
    const hoodCheckbox = document.getElementById('hood');

    // @ts-ignore
    hoodCheckbox.checked = this.#hoodState.isSelected;
    this.#showOrHideHoodOptions();
  }

  #updateHoodInsert() {
    const hoodInsert = document.getElementById('hood-insert');

    switch (this.#hoodState.insertChoice) {
      case HoodInsertChoice.ventAHoodMagicLung:
        hoodInsert[0].selected = true;
        break;
      case HoodInsertChoice.ventAHoodSingle:
        hoodInsert[1].selected = true;
        break;
      case HoodInsertChoice.ventAHoodDual:
        hoodInsert[2].selected = true;
        break;
      case HoodInsertChoice.modernAire:
        hoodInsert[3].selected = true;
        break;
    }
  }

  #updateHoodDuctCoverFinish() {
    const hoodDuctCoverFinish = document.getElementById('duct-cover-finish');

    switch (this.#hoodState.ductCoverFinish) {
      case HoodDuctCoverFinish.stainlessSteel:
        hoodDuctCoverFinish[0].selected = true;
        break;
      case HoodDuctCoverFinish.color:
        hoodDuctCoverFinish[1].selected = true;
        break;
    }
  }

  #updateHoodDuctCoverHeight() {
    const hoodDuctCoverHeight = document.getElementById('duct-cover-height');

    switch (this.#hoodState.ductCoverHeight) {
      case HoodDuctCoverHeight.twoFifty:
        hoodDuctCoverHeight[0].selected = true;
        break;
      case HoodDuctCoverHeight.fiveHundred:
        hoodDuctCoverHeight[1].selected = true;
        break;
      case HoodDuctCoverHeight.sevenFifty:
        hoodDuctCoverHeight[2].selected = true;
        break;
      case HoodDuctCoverHeight.oneThousand:
        hoodDuctCoverHeight[3].selected = true;
        break;
    }
  }

  #resetBurnerOptionSelections() {
    // @ts-ignore
    const farLeftOption = document.getElementById('far-left-option').options;
    // @ts-ignore
    const leftOption = document.getElementById('left-option').options;
    // @ts-ignore
    const rightOption = document.getElementById('right-option').options;
    // @ts-ignore
    const farRightOption = document.getElementById('far-right-option').options;

    farLeftOption[0].selected = true;
    leftOption[0].selected = true;
    rightOption[0].selected = true;
    farRightOption[0].selected = true;
  }

  #showOrHideSummaryTabs() {
    const desktopTabBar = document.getElementById(
      'summary-choices-for-desktop'
    );
    const mobileTabBar = document.getElementById('summary-choices-for-mobile');
    const rangeTabDesktop = document.getElementById('range-summary-tab');
    const hoodTabDesktop = document.getElementById('hood-summary-tab');
    const backsplashTabDesktop = document.getElementById(
      'backsplash-summary-tab'
    );
    const summarySelectMobile = document.getElementById(
      'summary-select-mobile'
    );
    const showTabs =
      (this.#rangeState.isSelected && this.#hoodState.isSelected) ||
      (this.#rangeState.isSelected && this.#backsplashState.isSelected) ||
      (this.#backsplashState.isSelected && this.#hoodState.isSelected);

    // Show or hide the entire summary navigation tab bar
    if (showTabs) {
      desktopTabBar.classList.add('sm:block');
      desktopTabBar.classList.remove('hidden');
      mobileTabBar.classList.add('grid');
      mobileTabBar.classList.remove('hidden');
    } else {
      desktopTabBar.classList.add('hidden');
      desktopTabBar.classList.remove('sm:block');
      mobileTabBar.classList.add('hidden');
      mobileTabBar.classList.remove('grid');

      if (this.#rangeState.isSelected) {
        this.#makeTabActive(Tab.range);
      } else if (this.#hoodState.isSelected) {
        this.#makeTabActive(Tab.hood);
      } else if (this.#backsplashState.isSelected) {
        this.#makeTabActive(Tab.backsplash);
      }
    }

    // Show or hide the range selector
    if (this.#rangeState.isSelected) {
      rangeTabDesktop.classList.remove('hidden');
      summarySelectMobile[0].disabled = false;
    } else {
      rangeTabDesktop.classList.add('hidden');
      summarySelectMobile[0].disabled = true;
    }

    // Show or hide the hood selector
    if (this.#hoodState.isSelected) {
      hoodTabDesktop.classList.remove('hidden');
      summarySelectMobile[1].disabled = false;
    } else {
      hoodTabDesktop.classList.add('hidden');
      summarySelectMobile[1].disabled = true;
    }

    // Show or hide the backsplash selector
    if (this.#backsplashState.isSelected) {
      backsplashTabDesktop.classList.remove('hidden');
      summarySelectMobile[2].disabled = false;
    } else {
      backsplashTabDesktop.classList.add('hidden');
      summarySelectMobile[2].disabled = true;
    }
  }

  /**
   * Make the range, hood or backsplash summary tab active
   * @param {Tab} tab
   */
  #makeTabActive(tab) {
    const rangeSummaryTabClasses =
      document.getElementById('range-summary-tab').classList;
    const hoodSummaryTabClasses =
      document.getElementById('hood-summary-tab').classList;
    const backsplashSummaryTabClasses = document.getElementById(
      'backsplash-summary-tab'
    ).classList;
    const rangeSummaryListClasses =
      document.getElementById('range-summary-list').classList;
    const hoodSummaryListClasses =
      document.getElementById('hood-summary-list').classList;
    const backsplashSummaryListClasses = document.getElementById(
      'backsplash-summary-list'
    ).classList;

    rangeSummaryTabClasses.remove('font-bold');
    hoodSummaryTabClasses.remove('font-bold');
    backsplashSummaryTabClasses.remove('font-bold');
    rangeSummaryTabClasses.add('font-medium');
    hoodSummaryTabClasses.add('font-medium');
    backsplashSummaryTabClasses.add('font-medium');

    rangeSummaryListClasses.add('hidden');
    hoodSummaryListClasses.add('hidden');
    backsplashSummaryListClasses.add('hidden');

    switch (tab) {
      case Tab.range:
        rangeSummaryTabClasses.remove(
          'border-transparent',
          '!text-gray-500',
          'font-medium'
        );
        rangeSummaryTabClasses.add(
          'border-darkRed',
          '!text-darkRed',
          'font-bold'
        );
        hoodSummaryTabClasses.remove('border-darkRed', '!text-darkRed');
        hoodSummaryTabClasses.add('border-transparent', '!text-gray-500');
        backsplashSummaryTabClasses.remove('border-darkRed', '!text-darkRed');
        backsplashSummaryTabClasses.add('border-transparent', '!text-gray-500');
        rangeSummaryListClasses.remove('hidden');
        break;

      case Tab.hood:
        hoodSummaryTabClasses.remove(
          'border-transparent',
          '!text-gray-500',
          'font-medium'
        );
        hoodSummaryTabClasses.add(
          'border-darkRed',
          '!text-darkRed',
          'font-bold'
        );
        rangeSummaryTabClasses.remove('border-darkRed', '!text-darkRed');
        rangeSummaryTabClasses.add('border-transparent', '!text-gray-500');
        backsplashSummaryTabClasses.remove('border-darkRed', '!text-darkRed');
        backsplashSummaryTabClasses.add('border-transparent', '!text-gray-500');
        hoodSummaryListClasses.remove('hidden');
        break;

      case Tab.backsplash:
        backsplashSummaryTabClasses.remove(
          'border-transparent',
          '!text-gray-500',
          'font-medium'
        );
        backsplashSummaryTabClasses.add(
          'border-darkRed',
          '!text-darkRed',
          'font-bold'
        );
        rangeSummaryTabClasses.remove('border-darkRed', '!text-darkRed');
        rangeSummaryTabClasses.add('border-transparent', '!text-gray-500');
        hoodSummaryTabClasses.remove('border-darkRed', '!text-darkRed');
        hoodSummaryTabClasses.add('border-transparent', '!text-gray-500');
        backsplashSummaryListClasses.remove('hidden');
        break;
    }
  }

  #updateBacksplashSummary() {
    const backsplashModelNumber = document.getElementById(
      'backsplash-model-number'
    );
    const backsplashWidth = document.getElementById('backsplash-width');

    backsplashModelNumber.innerText = this.#backsplashSummary.modelNumber();
    backsplashWidth.innerText = this.#backsplashSummary.width();
  }

  #updateHoodSummary() {
    const hoodModelNumber = document.getElementById('hood-model-number');
    const hoodColor = document.getElementById('hood-color');
    const hoodDuctCoverFinish = document.getElementById(
      'hood-duct-cover-finish'
    );
    const hoodDuctCoverHeight = document.getElementById(
      'hood-duct-cover-height'
    );
    const hoodWidth = document.getElementById('hood-width');
    const hoodInsertManufacturer = document.getElementById(
      'hood-insert-manufacturer'
    );
    const hoodInsertModelNumber = document.getElementById(
      'hood-insert-model-number'
    );
    const hoodInsertBlowerCapacity = document.getElementById(
      'hood-insert-blower-capacity'
    );

    hoodModelNumber.innerText = this.#hoodSummary.modelNumber();
    hoodColor.innerText = this.#hoodSummary.color();
    hoodDuctCoverFinish.innerText = this.#hoodSummary.ductCoverFinish();
    hoodDuctCoverHeight.innerText = this.#hoodSummary.ductCoverHeight();
    hoodWidth.innerText = this.#hoodSummary.hoodWidth();
    hoodInsertManufacturer.innerText = this.#hoodSummary.insertManufacturer();
    hoodInsertModelNumber.innerText = this.#hoodSummary.insertModelNumber();
    hoodInsertBlowerCapacity.innerText =
      this.#hoodSummary.insertBlowerCapacity();

    this.#updateSummary();
  }

  #updateSummary() {
    const summaryTitle = document.getElementById('summary-title');
    const summaryLink = document.getElementById('summary-link');
    const rangeSpecsLink = document.getElementById('range-specs-link');
    const rangeSpecsLink2 = document.getElementById('range-specs-link2');
    const installationPDFLink = document.getElementById('installation-pdf');

    summaryTitle.innerText = `Summary for ${this.#rangeSummary.model()} ${this.#rangeSummary.top()}`;

    // @ts-ignore
    summaryLink.href = this.#summary.summaryLink();
    // @ts-ignore
    rangeSpecsLink.href = this.#rangeSummary.specsLink();

    if (this.#url.isSummary()) {
      this.#update2DRangeImages();

      // @ts-ignore
      rangeSpecsLink2.href = this.#rangeSummary.specsLink();
      // @ts-ignore
      installationPDFLink.href =
        'https://zoho.frenchranges.com/specsheet/' + this.#rangeState.slug();
    }
  }

  #updateRangeSummary() {
    const rangeModel = document.getElementById('range-model');
    const rangeLine = document.getElementById('range-line');
    const rangeTop = document.getElementById('range-top');
    const rangeModelNumber = document.getElementById('range-model-number');
    const rangeTrim = document.getElementById('range-trim');
    const rangeColor = document.getElementById('range-color');
    const rangeLeftCupboardItem = document.getElementById(
      'range-left-cupboard-item'
    );
    const rangeLeftCupboard = document.getElementById('range-left-cupboard');
    const rangePetiteOvenItem = document.getElementById(
      'range-petite-oven-item'
    );
    const rangePetiteOven = document.getElementById('range-petite-oven');
    const rangeLeftOvenItem = document.getElementById('range-left-oven-item');
    const rangeLeftOven = document.getElementById('range-left-oven');
    const rangeRightOvenItem = document.getElementById('range-right-oven-item');
    const rangeRightOven = document.getElementById('range-right-oven');
    const rangeRightCupboardItem = document.getElementById(
      'range-right-cupboard-item'
    );
    const rangeRightCupboard = document.getElementById('range-right-cupboard');
    const rangeStorageDrawersItem = document.getElementById(
      'range-storage-drawers-item'
    );
    const rangeStorageDrawers = document.getElementById(
      'range-storage-drawers'
    );
    const rangeStandardBurner1 = document.getElementById(
      'range-standard-burner-1'
    );
    const rangeStandardBurner2 = document.getElementById(
      'range-standard-burner-2'
    );
    const rangeStandardBurner3 = document.getElementById(
      'range-standard-burner-3'
    );
    const rangeStandardBurner4 = document.getElementById(
      'range-standard-burner-4'
    );
    const rangeStandardBurner4Item = document.getElementById(
      'range-standard-burner-4-item'
    );
    const l1OptionBurnerItem = document.getElementById('l1-option-burner-item');
    const l1OptionBurner = document.getElementById('l1-option-burner');
    const l2OptionBurnerItem = document.getElementById('l2-option-burner-item');
    const l2OptionBurner = document.getElementById('l2-option-burner');
    const r1OptionBurnerItem = document.getElementById('r1-option-burner-item');
    const r1OptionBurner = document.getElementById('r1-option-burner');
    const r2OptionBurnerItem = document.getElementById('r2-option-burner-item');
    const r2OptionBurner = document.getElementById('r2-option-burner');
    const rangeGasType = document.getElementById('range-gas-type');
    const rangeHeight = document.getElementById('range-height');
    const rangeWidth = document.getElementById('range-width');
    const rangeAmps = document.getElementById('range-amps');
    const rangeWatts = document.getElementById('range-watts');
    const rangeBtusItem = document.getElementById('range-btus-item');
    const rangeBtus = document.getElementById('range-btus');

    rangeLeftCupboardItem.style.display = this.#rangeState.leftCupboard
      ? 'flex'
      : 'none';
    rangePetiteOvenItem.style.display = this.#rangeState.petiteOven
      ? 'flex'
      : 'none';
    rangeLeftOvenItem.style.display = this.#rangeState.leftOven
      ? 'flex'
      : 'none';
    rangeRightOvenItem.style.display = this.#rangeState.rightOven
      ? 'flex'
      : 'none';
    rangeRightCupboardItem.style.display = this.#rangeState.rightCupboard
      ? 'flex'
      : 'none';
    rangeStorageDrawersItem.style.display =
      this.#rangeState.storageDrawers > 0 ? 'flex' : 'none';
    l1OptionBurnerItem.style.display =
      this.#rangeSummary.l1Option() !== null ? 'flex' : 'none';
    l2OptionBurnerItem.style.display =
      this.#rangeSummary.l2Option() !== null ? 'flex' : 'none';
    rangeStandardBurner4Item.style.display =
      this.#rangeSummary.standardBurners(4) !== null ? 'flex' : 'none';
    r1OptionBurnerItem.style.display =
      this.#rangeSummary.r1Option() !== null ? 'flex' : 'none';
    r2OptionBurnerItem.style.display =
      this.#rangeSummary.r2Option() !== null ? 'flex' : 'none';
    rangeBtusItem.style.display =
      this.#rangeSummary.maxBtus() !== null ? 'flex' : 'none';

    rangeModel.innerText = 'Lacanche ' + this.#rangeSummary.model();
    rangeLine.innerText = this.#rangeSummary.line() + ' line';
    rangeTop.innerText = this.#rangeSummary.top() + ' range top';
    rangeModelNumber.innerText = this.#rangeSummary.modelNumber();
    rangeTrim.innerText = this.#rangeSummary.trim();
    rangeColor.innerText = this.#rangeSummary.color();
    rangeLeftCupboard.innerText = this.#rangeSummary.leftCupboard();
    rangePetiteOven.innerText = this.#rangeSummary.petiteOven();
    rangeLeftOven.innerText = this.#rangeSummary.leftOven();
    rangeRightOven.innerText = this.#rangeSummary.rightOven();
    rangeRightCupboard.innerText = this.#rangeSummary.rightCupboard();
    rangeStorageDrawers.innerText = this.#rangeSummary.storageDrawers();
    rangeStandardBurner1.innerText = this.#rangeSummary.standardBurners(1);
    rangeStandardBurner2.innerText = this.#rangeSummary.standardBurners(2);
    rangeStandardBurner3.innerText = this.#rangeSummary.standardBurners(3);
    rangeStandardBurner4.innerText = this.#rangeSummary.standardBurners(4);
    l1OptionBurner.innerText = this.#rangeSummary.l1Option();
    l2OptionBurner.innerText = this.#rangeSummary.l2Option();
    r1OptionBurner.innerText = this.#rangeSummary.r1Option();
    r2OptionBurner.innerText = this.#rangeSummary.r2Option();
    rangeGasType.innerText = this.#rangeSummary.gasType();
    rangeHeight.innerText = this.#rangeSummary.height();
    rangeWidth.innerText = this.#rangeSummary.width();
    rangeAmps.innerText = this.#rangeSummary.maxAmps();
    rangeWatts.innerText = this.#rangeSummary.maxWatts();
    rangeBtus.innerText = this.#rangeSummary.maxBtus();

    this.#updateSummary();
  }

  #update2DRangeImages() {
    const rangeFrontImage = document.getElementById('range-front-image');
    const rangeFrontImageLink = document.getElementById(
      'range-front-image-link'
    );
    const rangeTopImage = document.getElementById('range-top-image');
    const rangeTopImageLink = document.getElementById('range-top-image-link');

    // @ts-ignore
    rangeFrontImage.src = `/storage/${this.#rangeSummary.imageSlug(
      'front'
    )}.jpg`;
    // @ts-ignore
    rangeFrontImageLink.href = `/storage/${this.#rangeSummary.imageSlug(
      'front'
    )}.jpg`;
    // @ts-ignore
    rangeTopImage.src = `/storage/${this.#rangeSummary.imageSlug('top')}.jpg`;
    // @ts-ignore
    rangeTopImageLink.href = `/storage/${this.#rangeSummary.imageSlug(
      'top'
    )}.jpg`;
  }

  /**
   * Check or uncheck the Show Labels checkbox
   * @param {boolean} checked
   */
  #updateShowLabelsCheckbox(checked) {
    const showLabels = document.getElementById('show-labels');
    // @ts-ignore
    showLabels.checked = checked;
  }

  /**
   * Is the selected top too wide for the burner on its left?
   * @param {string} top
   * @returns {boolean}
   */
  #topHasIncompatibleBurnerToLeft(top) {
    switch (this.#rangeState.model) {
      case RangeModel.sully:
      case RangeModel.sully1800left:
      case RangeModel.sully1800right:
      case RangeModel.sully2200:
        if (this.#rules.hasWideBurnerInNarrowBurnerPosition({ top: top })) {
          this.#showDialog(Message.wideBurnerInNarrowPosition);
          this.#updateRangeTopSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Does the selected left option exceed the maximum amps?
   * @param {string} leftOption
   * @param {string} model
   * @returns {boolean}
   */
  #leftOptionExceedsMaxAmps(leftOption, model) {
    switch (model) {
      case RangeModel.cluny1800:
      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (this.#rules.isOverMaxAmps({ option2: leftOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateLeftOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (this.#rules.isOverMaxAmps({ option1: leftOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateLeftOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is the left option a Plancha over a gas oven?
   * @param {string} leftOption
   * @param {string} model
   * @returns {boolean}
   */
  #leftOptionIsPlanchaOverGasOven(leftOption, model) {
    switch (model) {
      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (this.#rules.hasPlanchaOverGasOven({ option2: leftOption })) {
          this.#showDialog(Message.planchaOverGasOven);
          this.#updateLeftOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (this.#rules.hasPlanchaOverGasOven({ option1: leftOption })) {
          this.#showDialog(Message.planchaOverGasOven);
          this.#updateLeftOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is the left option 2 Induction Rings over a gas oven?
   * @param {string} leftOption
   * @param {string} model
   * @returns {boolean}
   */
  #leftOptionIsInductionRingsOverGasOven(leftOption, model) {
    switch (model) {
      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (this.#rules.hasInductionRingsOverGasOven({ option2: leftOption })) {
          this.#showDialog(Message.inductionRingsOverGasOven);
          this.#updateLeftOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (this.#rules.hasInductionRingsOverGasOven({ option1: leftOption })) {
          this.#showDialog(Message.inductionRingsOverGasOven);
          this.#updateLeftOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Does the selected right option exceed the maximum amps?
   * @param {string} rightOption
   * @param {string} model
   * @returns {boolean}
   */
  #rightOptionExceedsMaxAmps(rightOption, model) {
    switch (model) {
      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (this.#rules.isOverMaxAmps({ option3: rightOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (this.#rules.isOverMaxAmps({ option2: rightOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is the right option a Plancha over a gas oven?
   * @param {string} rightOption
   * @param {string} model
   * @returns {boolean}
   */
  #rightOptionIsPlanchaOverGasOven(rightOption, model) {
    switch (model) {
      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (this.#rules.hasPlanchaOverGasOven({ option3: rightOption })) {
          this.#showDialog(Message.planchaOverGasOven);
          this.#updateRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (this.#rules.hasPlanchaOverGasOven({ option2: rightOption })) {
          this.#showDialog(Message.planchaOverGasOven);
          this.#updateRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is the right option 2 Induction Rings over a gas oven?
   * @param {string} rightOption
   * @param {string} model
   * @returns {boolean}
   */
  #rightOptionIsInductionRingsOverGasOven(rightOption, model) {
    switch (model) {
      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (
          this.#rules.hasInductionRingsOverGasOven({ option3: rightOption })
        ) {
          this.#showDialog(Message.inductionRingsOverGasOven);
          this.#updateRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (
          this.#rules.hasInductionRingsOverGasOven({ option2: rightOption })
        ) {
          this.#showDialog(Message.inductionRingsOverGasOven);
          this.#updateRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is the right option 2 Induction Rings over a gas oven?
   * @param {string} rightOption
   * @param {string} model
   * @returns {boolean}
   */
  #rightOptionIsTradPlateNextTo4Feux(rightOption, model) {
    switch (model) {
      case RangeModel.sully:
      case RangeModel.sully1800right:
        if (
          this.#rules.hasTraditionalPlateToRightOfSully4Feux({
            option2: rightOption,
          })
        ) {
          this.#showDialog(Message.traditionalPlateNextTo4Feux);
          this.#updateRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully1800left:
      case RangeModel.sully2200:
        if (
          this.#rules.hasTraditionalPlateToRightOfSully4Feux({
            option3: rightOption,
          })
        ) {
          this.#showDialog(Message.traditionalPlateNextTo4Feux);
          this.#updateRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Does the selected far right option exceed the maximum amps?
   * @param {string} farRightOption
   * @param {string} model
   * @returns {boolean}
   */
  #farRightOptionExceedsMaxAmps(farRightOption, model) {
    switch (model) {
      case RangeModel.chagny1400right:
      case RangeModel.chambertin:
      case RangeModel.cluny1400right:
      case RangeModel.savigny:
        if (this.#rules.isOverMaxAmps({ option1: farRightOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.chagny1800:
      case RangeModel.citeaux:
      case RangeModel.cluny1800:
      case RangeModel.fontenay:
        if (this.#rules.isOverMaxAmps({ option2: farRightOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully1800right:
        if (this.#rules.isOverMaxAmps({ option3: farRightOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully2200:
        if (this.#rules.isOverMaxAmps({ option4: farRightOption })) {
          this.#showDialog(Message.exceedsMaxRangeAmps);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is there a Multi-Cooker over a right warming cupboard?
   * @param {string} farRightOption
   * @param {string} model
   * @returns {boolean}
   */
  #farRightMultiCookerOverWarmingCupboard(farRightOption, model) {
    switch (model) {
      case RangeModel.chagny1400right:
      case RangeModel.chambertin:
      case RangeModel.cluny1400right:
      case RangeModel.savigny:
        if (
          this.#rules.hasMultiCookerOverWarmingCupboard({
            option1: farRightOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverWarmingCupboard);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.chagny1800:
      case RangeModel.citeaux:
      case RangeModel.cluny1800:
      case RangeModel.fontenay:
        if (
          this.#rules.hasMultiCookerOverWarmingCupboard({
            option2: farRightOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverWarmingCupboard);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully1800right:
        if (
          this.#rules.hasMultiCookerOverWarmingCupboard({
            option3: farRightOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverWarmingCupboard);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;

      case RangeModel.sully2200:
        if (
          this.#rules.hasMultiCookerOverWarmingCupboard({
            option4: farRightOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverWarmingCupboard);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is there a Multi-Cooker over a right vertical convection oven?
   * @param {string} farRightOption
   * @param {string} model
   * @returns {boolean}
   */
  #farRightMultiCookerOverVCT(farRightOption, model) {
    switch (model) {
      case RangeModel.savigny:
        if (
          this.#rules.hasMultiCookerOverVCT({
            option1: farRightOption,
          })
        ) {
          this.#showDialog(Message.multiCookerOverVCT);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Is there a Flame Grill over a right vertical convection oven?
   * @param {string} farRightOption
   * @param {string} model
   * @returns {boolean}
   */
  #farRightFlameGrillOverVCT(farRightOption, model) {
    switch (model) {
      case RangeModel.savigny:
        if (
          this.#rules.hasFlameGrillOverVCT({
            option1: farRightOption,
          })
        ) {
          this.#showDialog(Message.flameGrillOverVCT);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Are there prohibited Induction Rings on the far right?
   * @param {string} farRightOption
   * @param {string} model
   * @returns {boolean}
   */
  #farRightInductionRingsInProhibitedPositions(farRightOption, model) {
    switch (model) {
      case RangeModel.chambertin:
      case RangeModel.savigny:
        if (
          this.#rules.hasInductionRingsInProhibitedPositions({
            option1: farRightOption,
          })
        ) {
          this.#showDialog(Message.inductionRingsInProhibitedPosition);
          this.#updateFarRightOptionSelection();
          return true;
        }
        break;
    }

    return false;
  }

  /**
   * Show a modal dialog with the given message
   * @param {string} message
   */
  #showDialog(message) {
    // @ts-ignore
    const dialog = document.querySelector('dialog');
    const dialogText = document.getElementById('dialog-text');
    dialog.showModal();
    dialogText.innerText = message;
  }

  /**
   * Is the range wider than 1500mm?
   * @param {string} rangeModel
   */
  #isRangeWide(rangeModel) {
    return (
      rangeModel === RangeModel.cluny1800 ||
      rangeModel === RangeModel.chagny1800 ||
      rangeModel === RangeModel.sully1800left ||
      rangeModel === RangeModel.sully1800right ||
      rangeModel === RangeModel.sully2200
    );
  }

  /**
   * Set the default zoom level based on the range model
   * @param {string} rangeModel
   * @returns {number}
   */
  #rangeZoom(rangeModel) {
    const ranges1800 = [
      RangeModel.cluny1800,
      RangeModel.chagny1800,
      RangeModel.sully1800left,
      RangeModel.sully1800right,
    ];
    let zoom;

    if (rangeModel === RangeModel.sully2200) {
      zoom = 1.0;
    } else if (ranges1800.includes(rangeModel)) {
      zoom = 1.2;
    } else {
      zoom = 1.4;
    }

    if (window.innerWidth <= 640) {
      zoom /= 1.2;
    }

    return zoom;
  }

  /**
   * Set the camera position and zoom level to optimally show what's in the scene
   * @param {string} [rangeModel]
   */
  #setCameraPositionAndZoom(rangeModel = RangeModel.sully) {
    if (this.#url.isSummary()) {
      this.#defaultZoom = 1.0;
    } else {
      this.#defaultZoom = this.#rangeZoom(rangeModel);
    }

    // Adjust to show the range only
    if (
      this.#rangeState.isSelected &&
      !this.#hoodState.isSelected &&
      !this.#backsplashState.isSelected
    ) {
      this.#camera.position.set(0, 1.5, 4.5);
      this.#camera.zoom = this.#defaultZoom;
      this.#scene.position.y = -0.5;
      this.#camera.updateProjectionMatrix();
    }

    // Adjust to show the hood only
    if (
      !this.#rangeState.isSelected &&
      this.#hoodState.isSelected &&
      !this.#backsplashState.isSelected
    ) {
      this.#camera.position.set(0, 0, 4.5);
      this.#scene.position.y = -2.2;
    }

    // Adjust to show the backsplash only
    if (
      !this.#rangeState.isSelected &&
      !this.#hoodState.isSelected &&
      this.#backsplashState.isSelected
    ) {
      this.#camera.position.set(0, 0, 4.5);
      this.#scene.position.y = -1.35;
    }

    // Adjust to show the range and the hood (and maybe backsplash)
    if (this.#rangeState.isSelected && this.#hoodState.isSelected) {
      this.#camera.position.set(0, 1, 7);
      this.#scene.position.y = -1.4;
    }

    // Adjust to show the range and backsplash
    if (
      this.#rangeState.isSelected &&
      this.#backsplashState.isSelected &&
      !this.#hoodState.isSelected
    ) {
      this.#camera.position.set(0, 1, 5);
      this.#scene.position.y = -0.9;
    }

    // Adjust to show the hood and backsplash
    if (
      !this.#rangeState.isSelected &&
      this.#backsplashState.isSelected &&
      this.#hoodState.isSelected
    ) {
      this.#camera.position.set(0, 0, 5);
      this.#scene.position.y = -1.8;
    }
  }

  #setupSummaryPage() {
    // Add a 'summary' class to the main content to hide the sections that are not needed on the summary page
    document.getElementById('main_content').classList.add('summary');
    // Show the summary title that is hidden by default
    document.getElementById('summary-title').style.display = 'block';
    // Show the bottom half of the summary page that is hidden by default
    document.getElementById('req-form-block').style.display = 'flex';
    // Show the range front and top 2D images
    document.getElementById('range-2d-images').classList.add('sm:flex');
    // Show the summary page buttons
    document.getElementById('summary-buttons').classList.remove('hidden');
  }

  /** Turn a summary page into a regular configure range page */
  #undoSummaryPage() {
    const sceneContainerDiv = document.getElementById('scene-container');

    document.getElementById('main_content').classList.remove('summary');
    document.getElementById('summary-title').style.display = 'none';
    document.getElementById('range-2d-images').classList.remove('sm:flex');
    document.getElementById('summary-buttons').classList.add('hidden');

    this.#camera.zoom /= 1.4;
    this.#resizer.setSize();

    // Reset the URL
    history.pushState(null, '', '/');
  }

  #adjust3DModelViewForSummary() {
    if (!this.#url.isScreenshot()) {
      this.#setCameraPositionAndZoom();
    }

    // Range only
    if (
      this.#rangeState.isSelected &&
      !this.#backsplashState.isSelected &&
      !this.#hoodState.isSelected
    ) {
      this.#camera.zoom *= 1.4;
      this.#camera.updateProjectionMatrix();
    }

    // Hood only
    if (
      !this.#rangeState.isSelected &&
      !this.#backsplashState.isSelected &&
      this.#hoodState.isSelected
    ) {
      this.#camera.zoom *= 1.4;
      this.#camera.updateProjectionMatrix();
    }

    // Backsplash only
    if (
      !this.#rangeState.isSelected &&
      this.#backsplashState.isSelected &&
      !this.#hoodState.isSelected
    ) {
      this.#camera.zoom *= 1.7;
      this.#camera.updateProjectionMatrix();
    }

    // Range and hood (and maybe backsplash)
    if (this.#rangeState.isSelected && this.#hoodState.isSelected) {
      document
        .getElementById('scene-container')
        .classList.add('range-hood-3d-models');
      this.#camera.zoom *= 1.05;
      this.#resizer.setSize();
    }

    // Range and backsplash
    if (
      this.#rangeState.isSelected &&
      this.#backsplashState.isSelected &&
      !this.#hoodState.isSelected
    ) {
      document
        .getElementById('scene-container')
        .classList.add('range-backsplash-3d-models');
      this.#camera.zoom *= 1.1;
      this.#resizer.setSize();
    }

    // Hood and backsplash
    if (
      !this.#rangeState.isSelected &&
      this.#backsplashState.isSelected &&
      this.#hoodState.isSelected
    ) {
      document
        .getElementById('scene-container')
        .classList.add('hood-backsplash-3d-models');
      this.#camera.zoom *= 1.2;
      this.#resizer.setSize();
    }
  }

  /**
   * Add a camera helper
   * @param {Camera} camera
   */
  #addCameraHelper(camera) {
    const helper = new CameraHelper(camera);
    this.#scene.add(helper);
  }

  /**
   * Add a directional light helper
   * @param {DirectionalLight} directionalLight
   */
  #addDirectionalLightHelper(directionalLight) {
    const directionalLightHelper = new DirectionalLightHelper(directionalLight);
    this.#scene.add(directionalLightHelper);
  }

  /**
   * Add a shadow helper to a light
   * @param {Light} light
   */
  #addShadowHelper(light) {
    const helper = new CameraHelper(light.shadow.camera);
    this.#scene.add(helper);
  }
}
